import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useLanguage } from "./LanguageContext";
import { useUser } from "./UserContext";
import TopicRunModal from "./TopicRunModal";
import { apiFetch } from '../lib/apiFetch';
import { openAuditPdf } from "./AuditPdfDocument";
import KeyPromptsTab from "./KeyPromptsTab";

// Locale for date formatting, keyed by UI language
const DATE_LOCALE = { de: "de-CH", en: "en-GB", fr: "fr-CH", it: "it-CH" };

// ─── Styles ──────────────────────────────────────────────────────────────────
const css = `
  .dash-root {
    font-family: 'new-science', 'DM Sans', sans-serif;
  }

  /* ── Brand header ── */
  .dash-brand-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e2e8f0;
  }

  .dash-brand-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .dash-brand-favicon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 800;
    color: white;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(34,197,94,0.3);
  }

  .dash-brand-name {
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
  }

  .dash-brand-url {
    font-size: 13px;
    color: #64748b;
    margin: 0;
  }

  .dash-edit-btn {
    padding: 8px 16px;
    border-radius: 7px;
    border: 1.5px solid #e2e8f0;
    background: white;
    color: #0f172a;
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
  }
  .dash-edit-btn:hover { border-color: #94a3b8; background: #f8fafc; }

  /* ── Two Sides Hero ── */
  .ts-hero {
    display: grid;
    grid-template-columns: 1fr 100px 1fr;
    border: 1px solid #e2e8f0;
    margin-bottom: 28px;
    background: #e2e8f0;
    gap: 1px;
  }
  .ts-side-a { background: #f3f1ec; padding: 24px 28px; }
  .ts-side-b { background: #ffffff; padding: 24px 28px; }
  .ts-bridge {
    background: #0d0d0d; color: white;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 12px 8px; gap: 10px;
  }
  .ts-eyebrow {
    font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase;
    font-family: 'DM Mono', monospace; color: #64748b; margin-bottom: 12px;
  }
  .ts-score-big {
    font-size: 64px; line-height: 0.9; font-weight: 700;
    letter-spacing: -0.03em; color: #0d0d0d;
  }
  .ts-score-big.accent { color: #1f3df5; }
  .ts-score-denom { font-size: 22px; color: #94a3b8; font-weight: 400; }
  .ts-score-title { font-size: 16px; font-weight: 600; margin-top: 10px; color: #0d0d0d; }
  .ts-score-sub { font-size: 12px; color: #64748b; margin-top: 3px; }
  .ts-fixes { margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e8f0; }
  .ts-fix-row {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 12px; color: #334155; padding: 4px 0; gap: 8px;
  }
  .ts-fix-impact { font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 600; color: #16a34a; }
  .ts-open-pill {
    display: inline-block; padding: 2px 8px; font-size: 11px;
    font-family: 'DM Mono', monospace; border: 1px solid #fca5a5;
    color: #dc2626; background: #fef2f2; border-radius: 100px; margin-top: 8px;
  }
  .ts-bridge-label {
    font-size: 8px; letter-spacing: 0.08em; text-transform: uppercase;
    color: rgba(255,255,255,0.6); line-height: 1.5;
  }

  /* ── Actions Tab ── */
  .act-table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  .act-table th {
    text-align: left; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
    font-family: 'DM Mono', monospace; color: #94a3b8; padding: 8px 12px;
    border-bottom: 1px solid #e2e8f0; background: #f8fafc;
  }
  .act-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  .act-table tr:last-child td { border-bottom: none; }
  .act-cat-badge {
    display: inline-block; padding: 1px 7px; border-radius: 100px; font-size: 10px;
    font-family: 'DM Mono', monospace; border: 1px solid #e2e8f0; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  .act-status-select {
    padding: 3px 8px; border-radius: 6px; border: 1px solid #e2e8f0;
    font-size: 11px; font-family: inherit; background: white; cursor: pointer;
    color: #334155;
  }
  .act-status-open     { border-color: #e2e8f0; color: #64748b; }
  .act-status-in_progress { border-color: #fbbf24; color: #92400e; background: #fffbeb; }
  .act-status-done     { border-color: #86efac; color: #166534; background: #f0fdf4; }
  .act-status-ignore   { border-color: #e2e8f0; color: #94a3b8; background: #f8fafc; }
  .act-impact-pos { font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 600; color: #16a34a; }
  .act-impact-med { font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 600; color: #f59e0b; }
  .act-impact-low { font-family: 'DM Mono', monospace; font-size: 11px; color: #94a3b8; }

  /* ── Global KPI bar ── */
  .dash-global-kpis {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: 12px;
    margin-bottom: 32px;
    align-items: stretch;
  }
  .dash-kpi-card-tall {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    height: 100%;
  }

  .dash-kpi-card-tall .dash-kpi-value-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }
  .dash-kpi-right {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr 1fr;
    gap: 12px;
  }
  .dash-kpi-value-xl {
    font-size: 56px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -3px;
    margin: 12px 0 8px;
    color: #0f172a;
  }

  .dash-kpi-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 16px;
    transition: box-shadow 0.15s;
    display: flex;
    flex-direction: column;
  }
  .dash-kpi-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); }

  .dash-kpi-desc {
    font-size: 11px;
    color: #94a3b8;
    line-height: 1.45;
    margin-bottom: 6px;
    flex: 0 0 auto;
  }

  .dash-kpi-value-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
  }

  .dash-sparkline {
    margin-top: 10px;
    width: 100%;
    height: 32px;
  }

  .dash-sparkline-wrap {
    position: relative;
    margin-top: 10px;
    width: 100%;
    overflow: visible;
  }

  .dash-sparkline-tooltip {
    position: absolute;
    background: #0f172a;
    color: white;
    font-size: 11px;
    font-weight: 500;
    padding: 3px 7px;
    border-radius: 5px;
    pointer-events: none;
    white-space: nowrap;
    transform: translateX(-50%);
    bottom: calc(100% + 6px);
    opacity: 0;
    transition: opacity 0.1s;
    z-index: 10;
  }

  .dash-sparkline-tooltip.visible { opacity: 1; }

  .dash-sparkline-tooltip::after {
    content: "";
    position: absolute;
    top: 100%; left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #0f172a;
  }

  .dash-kpi-label {
    font-size: 11.5px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .dash-kpi-value {
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1;
    margin-bottom: 4px;
  }

  .dash-kpi-value.placeholder { color: #cbd5e1; }

  .dash-kpi-trend {
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .dash-kpi-trend.up   { color: #16a34a; }
  .dash-kpi-trend.down { color: #dc2626; }
  .dash-kpi-trend.neutral { color: #94a3b8; }

  /* ── Topic section ── */
  .dash-topic-section {
    margin-bottom: 24px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
  }

  .dash-topic-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #f1f5f9;
    background: #fafbfc;
  }

  .dash-topic-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
  }

  .dash-topic-icon {
    color: #64748b;
    display: flex;
  }

  .dash-topic-actions {
    display: flex;
    gap: 8px;
  }

  .dash-topic-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    background: white;
    font-family: 'new-science', 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    transition: all 0.15s;
  }
  .dash-topic-btn:hover { border-color: #94a3b8; color: #0f172a; background: #f8fafc; }
  .dash-topic-btn.primary { background: #2563eb; border-color: #2563eb; color: white; }
  .dash-topic-btn.primary:hover { background: #1d4ed8; }
  .dash-topic-btn.active { border-color: #2563eb; color: #2563eb; background: #eff6ff; }
  .dash-topic-btn.active:hover { background: #dbeafe; }

  .dash-topic-body {
    padding: 20px;
  }

  /* ── Widgets grid ── */
  .dash-widgets-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;
  }

  .dash-widget {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 14px 16px;
  }

  .dash-widget-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .dash-widget-value {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }
  .dash-widget-value.empty { color: #cbd5e1; font-size: 18px; }

  .dash-widget-sub {
    font-size: 11.5px;
    color: #94a3b8;
  }

  /* Sentiment bar */
  .dash-sentiment-bar {
    display: flex;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
    margin: 8px 0 4px;
    gap: 2px;
  }

  .dash-sentiment-pos  { background: #22c55e; border-radius: 2px; }
  .dash-sentiment-neu  { background: #f59e0b; border-radius: 2px; }
  .dash-sentiment-neg  { background: #ef4444; border-radius: 2px; }

  .dash-sentiment-legend {
    display: flex;
    gap: 10px;
    font-size: 11px;
    color: #64748b;
  }

  .dash-sentiment-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin-right: 3px;
    vertical-align: middle;
  }

  /* AI engine chips */
  .dash-engines {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 4px;
  }

  .dash-engine-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    border: 1px solid #e2e8f0;
    background: white;
    color: #334155;
  }

  .dash-engine-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  /* Search terms list */
  .dash-terms-preview {
    border-top: 1px solid #f1f5f9;
    padding-top: 14px;
    margin-top: 4px;
  }

  .dash-terms-label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
  }

  .dash-terms-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .dash-term-tag {
    padding: 4px 10px;
    background: #f1f5f9;
    border-radius: 100px;
    font-size: 12px;
    color: #475569;
    border: 1px solid #e2e8f0;
  }

  .dash-term-tag.no-data {
    color: #94a3b8;
    border-style: dashed;
  }

  /* ── Share of Voice ── */
  .dash-sov-wrap {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 16px;
    width: 220px;
    flex-shrink: 0;
  }
  .dash-sov-title {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
  }
  .dash-sov-sub {
    font-size: 11px;
    color: #94a3b8;
    margin: 0 0 10px;
  }
  .dash-sov-legend {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .dash-sov-legend-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    color: #475569;
  }
  .dash-sov-legend-left {
    display: flex;
    align-items: center;
    gap: 5px;
    overflow: hidden;
  }
  .dash-sov-legend-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
  }
  .dash-sov-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dash-sov-pct {
    font-weight: 700;
    color: #0f172a;
    flex-shrink: 0;
  }
  .dash-sov-link {
    font-size: 11px;
    color: #2563eb;
    cursor: pointer;
    margin-top: 8px;
    display: inline-block;
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;
  }
  .dash-sov-link:hover { text-decoration: underline; }
  .dash-topic-body-row {
    display: flex;
    gap: 14px;
    align-items: flex-start;
  }
  .dash-topic-kpis { flex: 1; min-width: 0; }

  /* Empty state */
  .dash-empty {
    text-align: center;
    padding: 48px 20px;
    color: #94a3b8;
  }

  .dash-empty-icon { font-size: 36px; margin-bottom: 12px; }
  .dash-empty-title { font-size: 15px; font-weight: 600; color: #64748b; margin-bottom: 6px; }
  .dash-empty-sub { font-size: 13px; }

  /* Loading */
  .dash-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #94a3b8;
    gap: 10px;
    font-size: 14px;
  }

  .dash-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid #e2e8f0;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: dashSpin 0.8s linear infinite;
  }

  @keyframes dashSpin { to { transform: rotate(360deg); } }

  @media (max-width: 900px) {
    .dash-global-kpis { grid-template-columns: repeat(2, 1fr); }
    .dash-widgets-grid { grid-template-columns: repeat(2, 1fr); }
  }

  /* ── Tab Navigation ── */
  .dash-tabs {
    display: flex;
    gap: 0;
    border-bottom: 2px solid #e2e8f0;
    margin-bottom: 28px;
  }
  .dash-tab {
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: color 0.15s, border-color 0.15s;
    background: none;
    border-top: none;
    border-left: none;
    border-right: none;
  }
  .dash-tab:hover { color: #2563eb; }
  .dash-tab.active { color: #2563eb; border-bottom-color: #2563eb; }

  /* ── Competitor Table ── */
  .comp-topic-section {
    margin-bottom: 28px;
  }
  .comp-topic-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 12px;
  }
  .comp-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
    font-size: 13px;
  }
  .comp-table thead tr {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  .comp-table th {
    padding: 10px 14px;
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
  .comp-table th:not(:first-child) { text-align: right; }
  .comp-table td {
    padding: 11px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
  }
  .comp-table td:not(:first-child) { text-align: right; }
  .comp-table tbody tr:last-child td { border-bottom: none; }
  .comp-table tbody tr:hover { background: #fafbff; }

  .comp-own-row { background: #f0f7ff !important; }
  .comp-own-row td { font-weight: 600; }

  .comp-rank-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #f1f5f9;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    margin-right: 8px;
  }
  .comp-own-badge {
    background: #dbeafe;
    color: #2563eb;
  }
  .comp-brand-name {
    font-weight: 600;
    color: #0f172a;
  }
  .comp-kpi-val { font-weight: 600; color: #0f172a; }
  .comp-kpi-good { color: #16a34a; }
  .comp-kpi-mid  { color: #f59e0b; }
  .comp-kpi-bad  { color: #dc2626; }

  .comp-loading {
    text-align: center;
    padding: 40px;
    color: #94a3b8;
    font-size: 14px;
  }
  .comp-divider-row td {
    padding: 6px 14px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;
    background: #fafbfc;
    border-bottom: 1px solid #e2e8f0;
  }

  /* ── Filter Bar ── */
  .filter-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    margin-bottom: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
  }

  .filter-time-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .filter-right-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  .filter-time-label,
  .filter-time-label-btn {
    height: 32px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    border: none;
    background: white;
    cursor: pointer;
    font-size: 13px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    color: #334155;
    transition: border-color 0.15s;
  }

  .filter-time-label-btn:hover {
    border-color: #94a3b8;
  }

  .filter-time-label-btn.active {
    border-color: #2563eb;
    color: #2563eb;
    background: #eff6ff;
  }

  .filter-time-label {
    height: 32px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-family: 'new-science','DM Sans',sans-serif;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    color: #0f172a;
    white-space: nowrap;
    min-width: 140px;
    justify-content: center;
  }

  .filter-time-badge {
    font-size: 11px;
    font-weight: 700;
    background: #2563eb;
    color: white;
    border-radius: 20px;
    padding: 1px 7px;
    line-height: 1.6;
  }

  .filter-nav-btn {
    height: 32px;
    min-width: 28px;
    padding: 0 8px;
    font-size: 15px;
    line-height: 1;
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    background: white;
    color: #475569;
    cursor: pointer;
    font-family: 'new-science','DM Sans',sans-serif;
    transition: background 0.15s;
  }
  .filter-nav-btn:hover:not(:disabled) { background: #f1f5f9; }
  .filter-nav-btn:disabled { opacity: 0.35; cursor: default; }
  .filter-nav-btn.active { background: #eff6ff; border-color: #2563eb; color: #2563eb; }

  .filter-config-wrap { position: relative; }

  .filter-config-popup {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.12);
    padding: 14px 16px;
    z-index: 300;
    min-width: 230px;
  }

  .filter-config-section-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    margin-bottom: 10px;
  }

  .filter-preset-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    margin-bottom: 14px;
  }

  .filter-preset-btn {
    padding: 6px 10px;
    font-size: 13px;
    font-family: 'new-science','DM Sans',sans-serif;
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    background: white;
    color: #475569;
    cursor: pointer;
    text-align: center;
    transition: all 0.15s;
  }
  .filter-preset-btn:hover { background: #f1f5f9; }
  .filter-preset-btn.active { background: #2563eb; border-color: #2563eb; color: white; }

  .filter-config-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #334155;
    cursor: pointer;
    padding-top: 10px;
    border-top: 1px solid #f1f5f9;
    user-select: none;
    font-family: 'new-science','DM Sans',sans-serif;
  }

  .filter-multiselect-wrap { position: relative; }

  .filter-multiselect-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 13px;
    font-family: 'new-science','DM Sans',sans-serif;
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    background: white;
    color: #0f172a;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.15s;
  }
  .filter-multiselect-btn.active { border-color: #2563eb; color: #2563eb; background: #eff6ff; }
  .filter-multiselect-btn:hover { background: #f1f5f9; }

  .dash-run-all-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 600;
    font-family: 'new-science','DM Sans',sans-serif;
    border: 1.5px solid #2563eb;
    border-radius: 7px;
    background: white;
    color: #2563eb;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
  }
  .dash-run-all-btn:hover { background: #eff6ff; }

  .filter-dropdown-panel {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 180px;
    max-height: 260px;
    overflow-y: auto;
    background: white;
    border: 1.5px solid #e2e8f0;
    border-radius: 9px;
    box-shadow: 0 6px 24px rgba(0,0,0,0.10);
    z-index: 200;
    padding: 6px 0;
  }

  .filter-dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    font-size: 13px;
    color: #334155;
    cursor: pointer;
    user-select: none;
  }
  .filter-dropdown-item:hover { background: #f1f5f9; }
  .filter-dropdown-item input[type="checkbox"] { cursor: pointer; accent-color: #2563eb; }

  /* ── Dashboard sections ── */
  .dash-section {
    margin-bottom: 24px;
  }
  .dash-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .dash-section-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #94a3b8;
    margin-bottom: 4px;
  }
  .dash-section-title {
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
    line-height: 1.2;
  }
  .dash-section-subtitle {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
  }

  /* ── Reichweite section ── */
  .dash-reach-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 12px;
  }
  .dash-reach-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 18px 20px 14px;
  }
  .dash-reach-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #64748b;
    margin-bottom: 6px;
  }
  .dash-reach-value {
    font-size: 32px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 4px;
    font-family: 'new-science','DM Sans',sans-serif;
  }
  .dash-reach-sub {
    font-size: 12px;
    color: #94a3b8;
    margin-bottom: 8px;
  }

  /* ── Qualität section ── */
  .dash-quality-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 180px 180px;
    gap: 12px;
  }
  .dash-quality-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 18px 20px 14px;
  }
  .dash-quality-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #64748b;
    margin-bottom: 14px;
  }
  .dash-details-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #2563eb;
    cursor: pointer;
    margin-top: 10px;
    text-decoration: none;
    background: none;
    border: none;
    padding: 0;
    font-family: 'new-science','DM Sans',sans-serif;
  }
  .dash-details-link:hover { text-decoration: underline; }

  .dash-details-panel {
    margin-top: 10px;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
    overflow: hidden;
    background: white;
  }
  .dash-details-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }
  .dash-details-table th {
    padding: 8px 12px;
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  .dash-details-table td {
    padding: 8px 12px;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
    max-width: 320px;
  }
  .dash-details-table tr:last-child td { border-bottom: none; }
  .dash-details-table tr:hover td { background: #f8fafc; }

  .dash-tier-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .dash-tier-empfohlen { background: #dcfce7; color: #16a34a; }
  .dash-tier-erklärt   { background: #dbeafe; color: #2563eb; }
  .dash-tier-erwähnt   { background: #f1f5f9; color: #64748b; }
  .dash-tier-positiv   { background: #dcfce7; color: #16a34a; }
  .dash-tier-neutral   { background: #fef9c3; color: #ca8a04; }
  .dash-tier-negativ   { background: #fee2e2; color: #dc2626; }

  /* ── Donut chart ── */
  .dash-donut-wrap {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .dash-donut-legend {
    flex: 1;
  }
  .dash-donut-legend-item {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    color: #334155;
    margin-bottom: 5px;
    line-height: 1.3;
  }
  .dash-donut-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .dash-donut-legend-pct {
    margin-left: auto;
    font-weight: 600;
    color: #0f172a;
    font-size: 11px;
  }

  /* ── Quellen section ── */
  .dash-sources-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .dash-engine-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .dash-engine-name {
    font-size: 12px;
    color: #334155;
    width: 80px;
    flex-shrink: 0;
  }
  .dash-engine-bar-wrap {
    flex: 1;
    height: 8px;
    background: #f1f5f9;
    border-radius: 4px;
    overflow: hidden;
  }
  .dash-engine-bar-fill {
    height: 100%;
    border-radius: 4px;
  }
  .dash-engine-pct {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    width: 32px;
    text-align: right;
    flex-shrink: 0;
  }

  /* ── Wettbewerb / Competition table ── */
  .dash-comp-section-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
  }
  .dash-comp-section-table th {
    padding: 10px 14px;
    text-align: left;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #94a3b8;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .dash-comp-section-table td {
    padding: 10px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    vertical-align: middle;
  }
  .dash-comp-section-table tr:last-child td { border-bottom: none; }
  .dash-comp-section-table tr.own-brand-row { background: #f0f7ff; }
  .dash-comp-section-table tr.own-brand-row td { font-weight: 600; color: #0f172a; }
  .dash-vis-bar-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .dash-vis-bar-track {
    width: 80px;
    height: 6px;
    background: #f1f5f9;
    border-radius: 3px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .dash-vis-bar-fill {
    height: 100%;
    border-radius: 3px;
    background: #2563eb;
  }
  .dash-own-star {
    color: #f59e0b;
    font-size: 14px;
  }
  .dash-no-data-section {
    text-align: center;
    padding: 32px;
    color: #94a3b8;
    font-size: 13px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
  }

  /* ===== Mobile responsive (≤768px) ===== */
  @media (max-width: 768px) {
    .dash-root {
      padding: 0 !important;
    }

    /* Brand header */
    .dash-brand-header {
      padding: 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 0;
      border-bottom: 1px solid #e2e8f0;
    }

    .dash-brand-left {
      width: 100%;
      gap: 12px;
    }

    .dash-brand-favicon {
      width: 40px;
      height: 40px;
      font-size: 16px;
    }

    .dash-brand-name {
      font-size: 18px;
      margin-bottom: 2px;
    }

    .dash-brand-url {
      font-size: 12px;
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      align-items: center;
    }

    /* Hide edit/delete button text, show only icons */
    .dash-edit-btn {
      width: auto !important;
      padding: 8px !important;
      font-size: 0;
      min-width: 36px;
    }

    .dash-edit-btn svg {
      display: inline;
    }

    /* Sections */
    .dash-section {
      padding: 0 !important;
      margin-bottom: 0 !important;
      border-radius: 0;
      border-top: 1px solid #f1f5f9;
      background: transparent;
    }

    .dash-section:first-of-type {
      border-top: none;
    }

    .dash-section-header {
      padding: 16px !important;
      border-bottom: 1px solid #f1f5f9;
    }

    .dash-section-label {
      font-size: 10px;
      margin-bottom: 4px;
      letter-spacing: 0.08em;
    }

    .dash-section-title {
      font-size: 18px;
      margin: 0 0 4px;
      font-weight: 700;
    }

    .dash-section-subtitle {
      font-size: 12px;
      color: #64748b;
      margin: 0;
    }

    /* Global KPIs */
    .dash-global-kpis {
      grid-template-columns: 1fr !important;
      gap: 0 !important;
      margin-bottom: 0 !important;
      padding: 0 16px 16px !important;
    }

    .dash-kpi-right {
      grid-template-columns: 1fr 1fr 1fr !important;
      grid-template-rows: auto;
      gap: 1px !important;
      background: #f1f5f9;
    }

    .dash-kpi-card-tall,
    .dash-kpi-card {
      border-radius: 0 !important;
      border: none !important;
      padding: 12px !important;
      font-size: 11px;
      background: white;
      min-height: auto;
    }

    /* Filterbar - responsive stacking */
    .filter-bar {
      flex-direction: column !important;
      flex-wrap: nowrap !important;
      gap: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      border-radius: 0 !important;
      border: none !important;
      background: transparent !important;
    }

    .filter-time-group {
      order: 1;
      display: flex !important;
      gap: 4px !important;
      padding: 8px 16px;
      flex-wrap: nowrap;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }

    .filter-time-group .filter-nav-btn {
      flex-shrink: 0;
    }

    .filter-time-group .filter-config-wrap {
      flex: 1;
      position: relative;
    }

    .filter-time-label-btn {
      width: 100%;
    }

    .filter-right-group {
      order: 2;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 8px !important;
      margin-left: 0 !important;
      padding: 12px 16px 8px;
      background: #f8fafc;
    }

    /* Keep 3 dropdowns on one line */
    .filter-multiselect-wrap {
      flex: 1;
      min-width: calc(33.333% - 6px);
      position: relative;
    }

    /* Full-width update button */
    .dash-run-all-btn {
      flex: 0 0 calc(100% + 16px) !important;
      order: 99;
      margin: 8px -16px -8px -16px !important;
      padding: 12px 16px !important;
      border-radius: 0 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
    }

    /* Reach grid (Sichtbarkeit section) - big score full width, 3 items in row below */
    .dash-reach-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr 1fr 1fr !important;
      gap: 12px !important;
      padding: 0 16px 16px !important;
      margin-bottom: 0 !important;
    }

    /* First card (big visibility score) spans all 4 columns */
    .dash-reach-grid > .dash-reach-card:first-child {
      grid-column: 1 / -1;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 18px 20px !important;
    }

    /* Next 3 cards (KPIs) - 3 columns on mobile */
    .dash-reach-grid > .dash-reach-card:nth-child(2),
    .dash-reach-grid > .dash-reach-card:nth-child(3),
    .dash-reach-grid > .dash-reach-card:nth-child(4) {
      grid-column: auto;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 14px 12px !important;
      background: white;
    }

    .dash-reach-label {
      font-size: 10px;
      margin-bottom: 4px;
    }

    .dash-reach-value {
      font-size: 24px;
      margin: 4px 0;
      font-weight: 800;
    }

    .dash-reach-sub {
      font-size: 10px;
    }

    /* Quality grid */
    .dash-quality-grid {
      grid-template-columns: 1fr 1fr !important;
      gap: 1px !important;
      background: #f1f5f9;
      padding: 0 16px 16px !important;
      margin-bottom: 0 !important;
    }

    .dash-quality-card {
      background: white;
      padding: 12px !important;
      border-radius: 0 !important;
      border: none !important;
    }

    /* Sources grid */
    .dash-sources-grid {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
      padding: 0 16px 16px !important;
    }

    .dash-sources-grid > div {
      border-radius: 8px;
      padding: 12px !important;
      border: 1px solid #e2e8f0;
    }

    /* Competition table - convert to card layout on mobile */
    .dash-comp-section-table {
      width: calc(100% - 32px) !important;
      margin: 0 16px 16px !important;
      border-collapse: separate;
      border-spacing: 0;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }

    .dash-comp-section-table thead {
      display: none;
    }

    .dash-comp-section-table tbody tr {
      display: grid;
      grid-template-columns: auto 1fr auto;
      gap: 8px;
      align-items: center;
      padding: 12px;
      border-bottom: 1px solid #f1f5f9;
    }

    .dash-comp-section-table tbody tr:last-child {
      border-bottom: none;
    }

    .dash-comp-section-table td {
      padding: 0 !important;
      font-size: 12px;
      border: none !important;
    }

    .dash-comp-section-table td:nth-child(1) {
      min-width: 24px;
      text-align: center;
      font-weight: 600;
      color: #475569;
    }

    .dash-comp-section-table td:nth-child(2) {
      font-weight: 500;
      color: #0f172a;
    }

    .dash-comp-section-table td:nth-child(3) {
      text-align: right;
      font-weight: 600;
      color: #0f172a;
    }

    /* Widgets */
    .dash-widgets-grid {
      grid-template-columns: 1fr !important;
      gap: 12px !important;
      padding: 0 16px 16px !important;
    }

    .dash-widget {
      border-radius: 8px;
      padding: 12px !important;
    }

    /* Buttons */
    .dash-update-vis-btn,
    .dash-new-eval-btn {
      width: calc(100% - 32px) !important;
      margin: 0 16px 16px !important;
    }

    /* Generic grid overrides */
    [style*="grid-template-columns: repeat(4"] {
      grid-template-columns: 1fr !important;
    }

    [style*="grid-template-columns: repeat(3"] {
      grid-template-columns: 1fr !important;
    }

    [style*="grid-template-columns: repeat(2"] {
      grid-template-columns: 1fr !important;
    }

    [style*="grid-template-columns: 1fr 1fr"] {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 640px) {
    /* Keep reach-grid with 3-column for KPI items below big score */
    .dash-reach-grid {
      grid-template-columns: 1fr 1fr 1fr !important;
    }

    .dash-quality-grid {
      grid-template-columns: 1fr !important;
    }

    .dash-brand-name {
      font-size: 16px;
    }

    .filter-time-group,
    .filter-right-group {
      min-width: 100% !important;
    }
  }
`;

// ─── Mock data generator (until real data exists) ────────────────────────────
function getMockKPIs() {
  return {
    visibility: null,
    ranking: null,
    sentiment: null,
    mentions: null,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SentimentWidget({ data, t }) {
  const pos = data?.pos ?? null;
  const neu = data?.neu ?? null;
  const neg = data?.neg ?? null;
  const hasData = pos !== null;

  return (
    <div className="dash-widget">
      <div className="dash-widget-label">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
        {t("dash.sentimentLabel")}
      </div>
      {hasData ? (
        <>
          <div className="dash-sentiment-bar">
            <div className="dash-sentiment-pos" style={{flex: pos}} />
            <div className="dash-sentiment-neu" style={{flex: neu}} />
            <div className="dash-sentiment-neg" style={{flex: neg}} />
          </div>
          <div className="dash-sentiment-legend">
            <span><span className="dash-sentiment-dot" style={{background:"#22c55e"}} />{pos}% pos</span>
            <span><span className="dash-sentiment-dot" style={{background:"#f59e0b"}} />{neu}% neu</span>
            <span><span className="dash-sentiment-dot" style={{background:"#ef4444"}} />{neg}% neg</span>
          </div>
        </>
      ) : (
        <>
          <div className="dash-widget-value empty">—</div>
          <div className="dash-widget-sub">{t("dash.noDataShort")}</div>
        </>
      )}
    </div>
  );
}

const PROVIDER_DISPLAY = { openai: "ChatGPT", claude: "Claude", gemini: "Gemini", perplexity: "Perplexity", copilot: "Copilot" };
const PROVIDER_COLOR   = { openai: "#10a37f", claude: "#d97706", gemini: "#4285f4", perplexity: "#6366f1", copilot: "#0078d4" };

function AIEngineWidget({ perProvider, t }) {
  const entries = Object.entries(perProvider || {}).filter(([, v]) => v !== null);
  const hasData = entries.length > 0;
  return (
    <div className="dash-widget" style={{ gridColumn: "span 1" }}>
      <div className="dash-widget-label">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        AI-Engines
      </div>
      {hasData ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
          {entries.map(([provider, val]) => {
            const color = PROVIDER_COLOR[provider] || "#64748b";
            return (
              <div key={provider} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, color: "#64748b", minWidth: 68 }}>{PROVIDER_DISPLAY[provider] || provider}</span>
                <div style={{ flex: 1, height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${val}%`, background: color, borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 30, textAlign: "right" }}>{val}%</span>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          <div className="dash-widget-value empty">—</div>
          <div className="dash-widget-sub">{t("dash.noDataShort")}</div>
        </>
      )}
    </div>
  );
}

function CompetitorMiniWidget({ competitors, onDetails, t }) {
  const hasData = competitors && competitors.length > 0;
  return (
    <div className="dash-widget" style={{ gridColumn: "span 1" }}>
      <div className="dash-widget-label">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        Mitbewerber – Sichtbarkeit
      </div>
      {hasData ? (
        <div style={{ marginTop: 4 }}>
          {competitors.slice(0, 6).map((comp, i) => {
            const color = comp.visibility >= 50 ? "#16a34a" : comp.visibility >= 20 ? "#f59e0b" : "#ef4444";
            const delta = comp.delta != null ? comp.delta : null;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: "#64748b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{comp.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 32, textAlign: "right" }}>{comp.visibility}%</span>
                {delta != null && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: delta > 0 ? "#16a34a" : delta < 0 ? "#ef4444" : "#94a3b8", minWidth: 36, textAlign: "right" }}>
                    {delta > 0 ? `↑+${delta}` : delta < 0 ? `↓${delta}` : "–"}
                  </span>
                )}
              </div>
            );
          })}
          {onDetails && (
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <button onClick={onDetails} style={{ background: "none", border: "none", color: "#2563eb", fontSize: 11, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                Details
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="dash-widget-value empty">—</div>
          <div className="dash-widget-sub">{t("dash.noDataShort")}</div>
        </>
      )}
    </div>
  );
}

function VisibilityWidget({ value, t }) {
  const color = value === null ? null : value >= 66 ? "#16a34a" : value >= 33 ? "#f59e0b" : "#dc2626";
  return (
    <div className="dash-widget">
      <div className="dash-widget-label">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
        </svg>
        {t("dash.visibilityLabel")}
      </div>
      <div className={`dash-widget-value ${value === null ? "empty" : ""}`} style={color ? {color} : {}}>
        {value !== null ? `${value}%` : "—"}
      </div>
      <div className="dash-widget-sub">{value !== null ? t("dash.visSubPos") : t("dash.noDataShort")}</div>
    </div>
  );
}

function RankingWidget({ value, t }) {
  const color = value === null ? null : value <= 2 ? "#16a34a" : value <= 3 ? "#f59e0b" : "#dc2626";
  return (
    <div className="dash-widget">
      <div className="dash-widget-label">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
        </svg>
        {t("dash.rankingLabel")}
      </div>
      <div className={`dash-widget-value ${value === null ? "empty" : ""}`} style={color ? {color} : {}}>
        {value !== null ? `#${value}` : "—"}
      </div>
      <div className="dash-widget-sub">{value !== null ? t("dash.rankSubPos") : t("dash.noDataShort")}</div>
    </div>
  );
}

function MentionsWidget({ value, total, t }) {
  return (
    <div className="dash-widget">
      <div className="dash-widget-label">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        {t("dash.mentionsLabel")}
      </div>
      <div className={`dash-widget-value ${value === null ? "empty" : ""}`}>
        {value !== null ? value : "—"}
      </div>
      <div className="dash-widget-sub">
        {value !== null ? `${t("common.loading").replace("…","")} ${total || "?"} AI` : t("dash.noDataShort")}
      </div>
    </div>
  );
}

function TopicSection({ topic, terms, kpis, competitors, onRefresh, onViewData, onTabSwitch, onDiscoveryToggle, t }) {
  const hasData = Object.values(kpis).some(v => v !== null);
  const [discoveryEnabled, setDiscoveryEnabled] = React.useState(topic.discovery_enabled || false);
  const [togglingDiscovery, setTogglingDiscovery] = React.useState(false);

  const handleDiscoveryToggle = async () => {
    setTogglingDiscovery(true);
    const next = !discoveryEnabled;
    try {
      await onDiscoveryToggle(topic.id, next);
      setDiscoveryEnabled(next);
    } finally {
      setTogglingDiscovery(false);
    }
  };

  return (
    <div className="dash-topic-section">
      {/* Header: title + search terms + actions */}
      <div className="dash-topic-header">
        <div style={{flex:1, minWidth:0}}>
          <div className="dash-topic-title">
            <span className="dash-topic-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
              </svg>
            </span>
            {t("dash.topicPrefix")}: {topic.name}
          </div>
          {/* 4) Search Terms in header */}
          {terms.length > 0 && (
            <div style={{display:"flex", alignItems:"center", gap:6, marginTop:6, flexWrap:"wrap"}}>
              <span style={{fontSize:10, fontWeight:600, color:"#94a3b8", letterSpacing:"0.05em", textTransform:"uppercase", flexShrink:0}}>
                {t("dash.searchTermsLabel")} ({terms.length})
              </span>
              {terms.slice(0, 6).map((term, i) => (
                <span key={i} className={`dash-term-tag ${!hasData ? "no-data" : ""}`} style={{fontSize:11, padding:"2px 8px"}}>
                  {term.term}
                </span>
              ))}
              {terms.length > 6 && (
                <span className="dash-term-tag" style={{color:"#94a3b8", fontSize:11}}>
                  +{terms.length - 6} {t("dash.more")}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="dash-topic-actions" style={{flexShrink:0}}>
          {/* Citation discovery toggle */}
          <button
            className={`dash-topic-btn${discoveryEnabled ? " active" : ""}`}
            onClick={handleDiscoveryToggle}
            disabled={togglingDiscovery}
            title={discoveryEnabled ? "Citation discovery enabled (≈doubles AI cost for this topic)" : "Enable 3rd-party citation discovery"}
            style={{ gap: 6 }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            {discoveryEnabled ? "Citations: ON" : "Citations: OFF"}
          </button>
          <button className="dash-topic-btn" onClick={() => onViewData && onViewData(topic, terms)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
            </svg>
            {t("dash.dataBtn")}
          </button>
          <button className="dash-topic-btn primary" onClick={() => onRefresh && onRefresh(topic, terms)}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
            </svg>
            {t("dash.refreshBtn")}
          </button>
        </div>
      </div>

      <div className="dash-topic-body">
        {/* Row 1: 4 KPI Widgets */}
        <div className="dash-widgets-grid">
          <VisibilityWidget value={kpis.visibility} t={t} />
          <RankingWidget    value={kpis.ranking} t={t} />
          <MentionsWidget   value={kpis.mentions} total={kpis.totalResponses} t={t} />
          <SentimentWidget  data={kpis.sentiment} t={t} />
        </div>
        {/* Row 2: Competitor mini-table + AI-Engine visibility */}
        <div className="dash-widgets-grid" style={{ marginTop: 12 }}>
          <CompetitorMiniWidget competitors={competitors} onDetails={onTabSwitch} t={t} />
          <AIEngineWidget perProvider={kpis.perProvider} t={t} />
        </div>
      </div>
    </div>
  );
}

// ─── Brand Performance Chart ──────────────────────────────────────────────────
const PERF_METRICS = [
  { key: "visibility",     label: "Visibility Score", unit: "%",  fmt: v => `${v}%`,     higher: true  },
  { key: "avgPosition",    label: "Avg. Position",    unit: "",   fmt: v => `#${v}`,     higher: false },
  { key: "top3Visibility", label: "Top 3 %",          unit: "%",  fmt: v => `${v}%`,     higher: true  },
  { key: "sentiment",      label: "Sentiment",        unit: "%",  fmt: v => `${v}%`,     higher: true  },
  { key: "citations",      label: "Citations",        unit: "",   fmt: v => String(v),   higher: true  },
  { key: "mentions",       label: "Mentions",         unit: "",   fmt: v => String(v),   higher: true  },
];

const BRAND_COLORS = [
  "#2563eb","#ef4444","#f97316","#eab308","#22c55e",
  "#06b6d4","#8b5cf6","#ec4899","#10b981","#f59e0b",
  "#3b82f6","#84cc16","#14b8a6","#a855f7","#fb923c",
  "#34d399","#60a5fa","#c084fc","#f87171","#fbbf24",
];

function BrandPerformanceChart({ brandName, ownBrand, competitors, history, onTabSwitch }) {
  const [metric, setMetric] = React.useState("visibility");
  const [hoverIdx, setHoverIdx] = React.useState(null);

  const metricDef = PERF_METRICS.find(m => m.key === metric);

  const allBrands = [
    { name: brandName, isOwn: true },
    ...competitors.slice(0, 19).map(c => ({ name: c.name, isOwn: false })),
  ];
  const colorMap = allBrands.reduce((m, b, i) => ({ ...m, [b.name]: BRAND_COLORS[i] }), {});

  // Synthesize a single history point from competitors prop when server history is empty
  const effectiveHistory = React.useMemo(() => {
    if (history?.length > 0) return history;
    if (competitors.length === 0) return [];
    const brands = {};
    brands[brandName] = {
      visibility:     ownBrand?.visibility ?? null,
      avgPosition:    ownBrand?.ranking ?? null,
      top3Visibility: ownBrand?.top3Visibility ?? null,
      sentiment:      typeof ownBrand?.sentiment === "object" ? (ownBrand.sentiment?.score ?? null) : (ownBrand?.sentiment ?? null),
      citations:      ownBrand?.citations ?? null,
      mentions:       ownBrand?.mentions ?? null,
    };
    competitors.slice(0, 19).forEach(c => {
      brands[c.name] = {
        visibility:     c.visibility ?? null,
        avgPosition:    c.avgPosition ?? null,
        top3Visibility: c.top3Visibility ?? null,
        sentiment:      c.sentiment ?? null,
        citations:      c.citations ?? null,
        mentions:       c.mentions ?? null,
      };
    });
    return [{ date: new Date().toISOString(), brands }];
  }, [history, competitors, brandName, ownBrand]);

  const validHistory = effectiveHistory.filter(pt =>
    pt.brands && allBrands.some(b => pt.brands[b.name]?.[metric] != null)
  );

  const displayIdx = hoverIdx != null ? hoverIdx : (validHistory.length - 1);
  const displayPoint = validHistory[displayIdx] || null;
  const prevPoint = displayIdx > 0 ? validHistory[displayIdx - 1] : null;

  const fmtDate = (d) => {
    if (!d) return "";
    const m = d.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return d;
    const dt = new Date(+m[1], +m[2] - 1, +m[3]);
    const jan1 = new Date(dt.getFullYear(), 0, 1);
    const week = Math.ceil(((dt - jan1) / 86400000 + jan1.getDay() + 1) / 7);
    return `W${String(week).padStart(2, "0")} ${m[1]}`;
  };

  const fmtVal = (v) => (v == null ? "—" : metricDef.fmt(v));

  const W = 420, H = 220, PAD = { t: 12, b: 26, l: 36, r: 8 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;

  const allVals = validHistory.flatMap(pt => allBrands.map(b => pt.brands?.[b.name]?.[metric]).filter(v => v != null));
  const rawMin = allVals.length ? Math.min(...allVals) : 0;
  const rawMax = allVals.length ? Math.max(...allVals) : 100;
  const pad = (rawMax - rawMin) * 0.1 || 5;
  const minVal = Math.max(0, rawMin - pad);
  const maxVal = rawMax + pad;
  const range = maxVal - minVal || 1;

  const xScale = i => PAD.l + (i / Math.max(validHistory.length - 1, 1)) * chartW;
  const yScale = v => PAD.t + chartH - ((v - minVal) / range) * chartH;

  return (
    <div style={{ background:"white", border:"1px solid #e2e8f0", borderRadius:10, padding:"16px 20px", marginTop:14, minHeight:280, maxWidth:1100 }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>Brand Performance Over Time</div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <select
            value={metric}
            onChange={e => setMetric(e.target.value)}
            style={{
              fontSize:12, fontWeight:600, color:"#334155",
              border:"1px solid #e2e8f0", borderRadius:6,
              padding:"4px 24px 4px 8px", background:"white", cursor:"pointer",
              appearance:"none",
              backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
              backgroundRepeat:"no-repeat", backgroundPosition:"right 6px center",
            }}
          >
            {PERF_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          {onTabSwitch && (
            <button className="dash-sov-link" onClick={onTabSwitch} style={{ margin:0 }}>Details →</button>
          )}
        </div>
      </div>

      <div style={{ display:"flex", gap:20, alignItems:"stretch" }}>
        {/* Chart */}
        <div style={{ flex:1, minWidth:0, minHeight:H }}>
          {validHistory.length >= 2 ? (
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"100%", minHeight:H, display:"block", overflow:"visible" }}
              onMouseLeave={() => setHoverIdx(null)}>
              {/* Grid lines */}
              {[0.25,0.5,0.75,1].map((t,i) => (
                <line key={i} x1={PAD.l} y1={yScale(minVal + t*range)} x2={W-PAD.r} y2={yScale(minVal + t*range)}
                  stroke="#f1f5f9" strokeWidth="1" />
              ))}
              {/* Y labels */}
              {[0,0.5,1].map((t,i) => {
                const v = minVal + t * range;
                const lbl = metric === "avgPosition" ? `#${Math.round(v)}` : metricDef.unit === "%" ? `${Math.round(v)}%` : Math.round(v);
                return <text key={i} x={PAD.l-3} y={yScale(v)+3} textAnchor="end" fontSize="8" fill="#94a3b8">{lbl}</text>;
              })}
              {/* Lines */}
              {allBrands.map((brand, bi) => {
                const segments = [];
                let cur = [];
                validHistory.forEach((pt, i) => {
                  const v = pt.brands?.[brand.name]?.[metric];
                  if (v == null) { if (cur.length) { segments.push(cur); cur = []; } }
                  else cur.push({ x: xScale(i), y: yScale(v) });
                });
                if (cur.length) segments.push(cur);
                return segments.map((seg, si) => (
                  <path key={`${bi}-${si}`}
                    d={seg.map((p, pi) => `${pi===0?"M":"L"}${p.x},${p.y}`).join(" ")}
                    fill="none" stroke={colorMap[brand.name]}
                    strokeWidth={brand.isOwn ? 2.2 : 1.4}
                    strokeLinecap="round" strokeLinejoin="round"
                    opacity={brand.isOwn ? 1 : 0.75}
                  />
                ));
              })}
              {/* Invisible hover zones */}
              {validHistory.map((_, i) => {
                const segW = chartW / Math.max(validHistory.length - 1, 1);
                return (
                  <rect key={i}
                    x={xScale(i) - segW / 2} width={segW}
                    y={PAD.t} height={chartH}
                    fill="transparent"
                    onMouseEnter={() => setHoverIdx(i)}
                  />
                );
              })}
              {/* Hover line */}
              {hoverIdx != null && (
                <line x1={xScale(hoverIdx)} y1={PAD.t} x2={xScale(hoverIdx)} y2={PAD.t+chartH}
                  stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,2" />
              )}
              {/* Hover dots */}
              {hoverIdx != null && allBrands.map((brand, bi) => {
                const v = validHistory[hoverIdx]?.brands?.[brand.name]?.[metric];
                if (v == null) return null;
                return <circle key={bi} cx={xScale(hoverIdx)} cy={yScale(v)} r={brand.isOwn ? 4 : 3}
                  fill="white" stroke={colorMap[brand.name]} strokeWidth="2" />;
              })}
              {/* X labels */}
              {validHistory.map((pt, i) => {
                if (validHistory.length > 6 && i % Math.ceil(validHistory.length / 5) !== 0 && i !== validHistory.length - 1) return null;
                return <text key={i} x={xScale(i)} y={H-3} textAnchor="middle" fontSize="8" fill="#94a3b8">{fmtDate(pt.date)}</text>;
              })}
            </svg>
          ) : validHistory.length === 1 ? (
            // Single run: horizontal dashed line per brand
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"100%", minHeight:H, display:"block" }}>
              {[0,0.5,1].map((t,i) => {
                const v = minVal + t * range;
                const lbl = metric === "avgPosition" ? `#${Math.round(v)}` : metricDef.unit === "%" ? `${Math.round(v)}%` : Math.round(v);
                return <text key={i} x={PAD.l-3} y={yScale(v)+3} textAnchor="end" fontSize="8" fill="#94a3b8">{lbl}</text>;
              })}
              {allBrands.map((brand, bi) => {
                const v = validHistory[0]?.brands?.[brand.name]?.[metric];
                if (v == null) return null;
                const cy = yScale(v);
                const cx = (PAD.l + W - PAD.r) / 2;
                return (
                  <g key={bi}>
                    <line x1={PAD.l + 4} y1={cy} x2={W - PAD.r - 4} y2={cy}
                      stroke={colorMap[brand.name]} strokeWidth={brand.isOwn ? 2 : 1.2}
                      strokeDasharray="6,3" opacity={brand.isOwn ? 1 : 0.65} />
                    <circle cx={cx} cy={cy} r={brand.isOwn ? 5 : 3.5}
                      fill="white" stroke={colorMap[brand.name]} strokeWidth="2" />
                  </g>
                );
              })}
              <text x={(PAD.l + W - PAD.r) / 2} y={H - 3} textAnchor="middle" fontSize="8" fill="#94a3b8">
                {fmtDate(validHistory[0]?.date)}
              </text>
            </svg>
          ) : (
            <div style={{ fontSize:11, color:"#94a3b8", padding:"20px 0" }}>
              Führe zuerst einen Run durch um Daten zu sehen.
            </div>
          )}
        </div>

        {/* Right panel: Latest Values */}
        <div style={{ flexBasis:"28%", minWidth:195, maxWidth:320, flexShrink:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4 }}>
            Latest Values
          </div>
          {displayPoint && (
            <div style={{ fontSize:10, color:"#94a3b8", marginBottom:8 }}>{fmtDate(displayPoint.date)}</div>
          )}

          {/* Own brand */}
          {(() => {
            const val = displayPoint?.brands?.[brandName]?.[metric];
            const prev = prevPoint?.brands?.[brandName]?.[metric];
            const rawDelta = val != null && prev != null ? val - prev : null;
            const delta = rawDelta != null ? Math.round(rawDelta * 10) / 10 : null;
            const positive = delta != null && (metricDef.higher ? delta > 0 : delta < 0);
            const negative = delta != null && (metricDef.higher ? delta < 0 : delta > 0);
            return (
              <div style={{ padding:"6px 8px", background:"#eff6ff", borderRadius:6, marginBottom:6,
                display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ display:"flex", alignItems:"center", gap:5, flex:1, minWidth:0 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:colorMap[brandName], flexShrink:0 }}/>
                  <span style={{ fontSize:11, fontWeight:700, color:"#2563eb", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {brandName}
                  </span>
                  <span style={{ fontSize:10, color:"#94a3b8", fontWeight:400, flexShrink:0 }}>(Your Brand)</span>
                </div>
                <div style={{ display:"flex", gap:3, alignItems:"center", flexShrink:0, marginLeft:4 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>{fmtVal(val)}</span>
                  {delta != null && delta !== 0 && (
                    <span style={{ fontSize:10, color: positive ? "#16a34a" : negative ? "#dc2626" : "#94a3b8" }}>
                      {delta > 0 ? "↑" : "↓"}{Math.abs(delta).toFixed(1)}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          <div style={{ fontSize:9, fontWeight:700, color:"#94a3b8", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
            Competitors
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:2, maxHeight:300, overflowY:"auto" }}>
            {competitors.slice(0, 19).map((comp, i) => {
              const val = displayPoint?.brands?.[comp.name]?.[metric];
              const prev = prevPoint?.brands?.[comp.name]?.[metric];
              const rawDelta = val != null && prev != null ? val - prev : null;
              const delta = rawDelta != null ? Math.round(rawDelta * 10) / 10 : null;
              const positive = delta != null && (metricDef.higher ? delta > 0 : delta < 0);
              const negative = delta != null && (metricDef.higher ? delta < 0 : delta > 0);
              return (
                <div key={i} style={{ padding:"4px 6px", borderRadius:4,
                  display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:5, flex:1, minWidth:0 }}>
                    <div style={{ width:7, height:7, borderRadius:"50%", background:colorMap[comp.name], flexShrink:0 }}/>
                    <span style={{ fontSize:11, color:"#334155", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {comp.name}
                    </span>
                  </div>
                  <div style={{ display:"flex", gap:3, alignItems:"center", flexShrink:0, marginLeft:4 }}>
                    <span style={{ fontSize:11, fontWeight:600, color:"#0f172a" }}>{fmtVal(val)}</span>
                    {delta != null && delta !== 0 && (
                      <span style={{ fontSize:10, color: positive ? "#16a34a" : negative ? "#dc2626" : "#94a3b8" }}>
                        {delta > 0 ? `↑+${Math.abs(delta).toFixed(1)}` : `↓-${Math.abs(delta).toFixed(1)}`}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            {competitors.length === 0 && (
              <div style={{ fontSize:11, color:"#94a3b8" }}>Keine Mitbewerber gefunden</div>
            )}
          </div>

          <div style={{ fontSize:9, color:"#94a3b8", marginTop:8 }}>
            Up to 20 brands are displayed in the graph
          </div>
        </div>
      </div>
    </div>
  );
}


function CompetitorTab({ brandId, brandName, t }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    apiFetch(`/competitors?brandId=${brandId}${force ? "&force=true" : ""}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); setRefreshing(false); })
      .catch(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { loadData(); }, [brandId]);

  if (loading) return (
    <div className="comp-loading">
      <div className="dash-spinner" style={{display:"inline-block", marginRight:10}} />
      Mitbewerber werden analysiert…
    </div>
  );

  if (!data?.topics?.length) return (
    <div className="dash-empty">
      <div className="dash-empty-icon">🔍</div>
      <div className="dash-empty-title">Keine Daten verfügbar</div>
      <div className="dash-empty-sub">Starte zuerst einen Run um Mitbewerber zu analysieren.</div>
      <button onClick={() => loadData(true)} disabled={refreshing} style={{
        marginTop:16, padding:"8px 18px", borderRadius:6, border:"1px solid #e2e8f0",
        background:"#fff", color:"#2563eb", fontWeight:600, fontSize:13, cursor:"pointer"
      }}>
        {refreshing ? "Wird aktualisiert…" : "Neu analysieren"}
      </button>
    </div>
  );

  return (
    <div>
      <div style={{marginBottom:20, display:"flex", alignItems:"flex-start", justifyContent:"space-between"}}>
        <div>
          <h2 style={{fontSize:18,fontWeight:700,color:"#0f172a",margin:"0 0 4px"}}>
            Mitbewerber-Analyse
          </h2>
          <p style={{fontSize:13,color:"#64748b",margin:0}}>
            Marken die in den AI-Antworten erwähnt werden, automatisch erkannt.
          </p>
        </div>
        <button onClick={() => loadData(true)} disabled={refreshing} style={{
          padding:"6px 14px", borderRadius:6, border:"1px solid #e2e8f0",
          background:"#fff", color:"#2563eb", fontWeight:600, fontSize:13,
          cursor: refreshing ? "default" : "pointer", flexShrink:0, marginTop:2
        }}>
          {refreshing ? "Wird aktualisiert…" : "Neu analysieren"}
        </button>
      </div>

      {data.topics.map(topic => (
        <div key={topic.id} className="comp-topic-section">
          <div className="comp-topic-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>
            </svg>
            Topic: {topic.name}
          </div>

          {!topic.ownBrand ? (
            <div style={{color:"#94a3b8",fontSize:13,padding:"12px 0"}}>Keine Daten für dieses Topic.</div>
          ) : (() => {
            const kpiColor = (val, high=50, low=20) => val==null?"":val>=high?"comp-kpi-good":val>=low?"comp-kpi-mid":"comp-kpi-bad";
            const posColor = (val) => !val?"":val<=2?"comp-kpi-good":val<=3.5?"comp-kpi-mid":"comp-kpi-bad";
            const sentColor = (val) => val==null?"":val>=60?"comp-kpi-good":val>=40?"comp-kpi-mid":"comp-kpi-bad";
            return (<>
              <table className="comp-table" style={{marginBottom:20}}>
                <thead>
                  <tr>
                    <th style={{width:40}}>#</th>
                    <th>Marke</th>
                    <th title="Ø Listenposition (1=beste)">Avg. Pos.</th>
                    <th title="% Antworten wo Marke in Top 3 der Liste">Top 3</th>
                    <th title="Sentiment der Erwähnungen (50=neutral)">Sentiment</th>

                    <th title="Anzahl Antworten die Marke nennen">Mentions</th>
                    <th title="% aller Antworten die Marke nennen">Visibility</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="comp-own-row">
                    <td><span className="comp-rank-badge comp-own-badge">★</span></td>
                    <td><span className="comp-brand-name">{topic.ownBrand.name}</span></td>
                    <td><span className={posColor(topic.ownBrand.avgPosition)}>{topic.ownBrand.avgPosition!=null?`#${topic.ownBrand.avgPosition}`:"—"}</span></td>
                    <td><span className={kpiColor(topic.ownBrand.top3Visibility)}>{topic.ownBrand.top3Visibility}%</span></td>
                    <td><span className={sentColor(topic.ownBrand.sentiment)}>{topic.ownBrand.sentiment??50}%</span></td>
                    <td>{topic.ownBrand.mentions}</td>
                    <td><strong className={kpiColor(topic.ownBrand.visibility)}>{topic.ownBrand.visibility}%</strong></td>
                  </tr>
                  {topic.competitors.length > 0 && (
                    <tr className="comp-divider-row"><td colSpan={8}>Mitbewerber — sortiert nach Visibility · Avg. Position · Alphabet</td></tr>
                  )}
                  {topic.competitors.map((comp, i) => (
                    <tr key={i}>
                      <td><span className="comp-rank-badge">{i+1}</span></td>
                      <td><span className="comp-brand-name">{comp.name}</span></td>
                      <td><span className={posColor(comp.avgPosition)}>{comp.avgPosition!=null?`#${comp.avgPosition}`:"—"}</span></td>
                      <td><span className={kpiColor(comp.top3Visibility)}>{comp.top3Visibility}%</span></td>
                      <td><span className={sentColor(comp.sentiment)}>{comp.sentiment??50}%</span></td>
                      <td>{comp.mentions}</td>
                      <td><strong className={kpiColor(comp.visibility)}>{comp.visibility}%</strong></td>
                    </tr>
                  ))}
                  {topic.competitors.length === 0 && (
                    <tr><td colSpan={8} style={{textAlign:"center",color:"#94a3b8",padding:"20px"}}>Keine Mitbewerber in den Antworten gefunden.</td></tr>
                  )}
                </tbody>
              </table>
              <BrandPerformanceChart
                brandName={brandName}
                ownBrand={topic.ownBrand}
                competitors={topic.competitors}
                history={topic.history}
              />
            </>);
          })()}
        </div>
      ))}
    </div>
  );
}



// ─── Reusable KPI Card ─────────────────────────────────────────────────────
const ICONS = {
  trend: <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/>,
  chat:  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,
  smile: <><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></>,
  link:  <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
  podium:<><path d="M8 6v14M16 2v18M4 10v10M12 8v12"/></>,
  radar: <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
};

// ── Sparkline SVG component with hover tooltip ──
function Sparkline({ data, color = "#2563eb", height = 32, unit = "%" }) {
  const [tooltip, setTooltip] = useState(null);
  if (!data || data.length < 2) return null;

  const vals = data.map(d => d.visibility);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const PAD = 6;
  const w = 100, h = height;

  const points = vals.map((v, i) => ({
    xPct: (i / (vals.length - 1)) * 100,
    y: h - ((v - min) / range) * (h - PAD * 2) - PAD,
    v,
    date: data[i].date,
  }));

  const ptsStr = points.map(p => `${(p.xPct / 100) * w},${p.y}`).join(" ");

  const formatDate = (dateStr) => {
    const match = dateStr?.match(/(\d{4})-(\d{2})-(\d{2})T?(\d{2}:\d{2})?/);
    return match ? `${match[3]}.${match[2]}.${match[1]}${match[4] ? " " + match[4] : ""}` : dateStr;
  };

  return (
    <div className="dash-sparkline-wrap" style={{position:"relative", marginTop:10, width:"100%"}}>
      {tooltip && (
        <div className="dash-sparkline-tooltip visible" style={{left:`${tooltip.xPct}%`}}>
          {unit === "%" ? `${tooltip.v}%` : unit === "#" ? `#${tooltip.v}` : tooltip.v} ({formatDate(tooltip.date)})
        </div>
      )}
      {/* SVG line — no circles, so we can use preserveAspectRatio="none" safely */}
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
        style={{width:"100%", height, display:"block", overflow:"visible"}}>
        <polyline points={ptsStr} fill="none" stroke={color} strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Circles as absolutely positioned divs — always fixed 10px, never scaled */}
      {points.map((p, i) => (
        <div key={i}
          onMouseEnter={() => setTooltip({ xPct: p.xPct, v: p.v, date: p.date })}
          onMouseLeave={() => setTooltip(null)}
          style={{
            position: "absolute",
            width: 10, height: 10,
            borderRadius: "50%",
            background: "white",
            border: `2px solid ${color}`,
            cursor: "pointer",
            // Center on the point: left% minus 5px, top in px minus 5px
            left: `calc(${p.xPct}% - 5px)`,
            top: `calc(${(p.y / h) * 100}% - 5px)`,
            boxSizing: "border-box",
          }}
        />
      ))}
    </div>
  );
}

function KpiCard({ label, icon, value, trend, trendLabel, color, noDataLabel, description, history, unit = "%" }) {
  const hasHistory = history && history.length >= 2;
  return (
    <div className="dash-kpi-card">
      <div className="dash-kpi-label">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          {ICONS[icon]}
        </svg>
        {label}
      </div>
      {description && <div className="dash-kpi-desc">{description}</div>}
      <div className="dash-kpi-value-wrap">
        <div className={`dash-kpi-value ${value === null ? "placeholder" : ""}`}>
          {value !== null ? value : "—"}
        </div>
        <div className={`dash-kpi-trend ${value === null ? "neutral" : trend || "neutral"}`} style={color && value !== null ? {color} : {}}>
          {value === null ? (noDataLabel || "—") : (trendLabel || "")}
        </div>
        {hasHistory && <Sparkline data={history} color={color || "#2563eb"} unit={unit} />}
      </div>
    </div>
  );
}

// ─── DonutChart component ────────────────────────────────────────────────────
function DonutChart({ segments }) {
  const r = 34, cx = 40, cy = 40, stroke = 10;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + (seg.value || 0), 0);

  if (!total) {
    return (
      <div className="dash-donut-wrap">
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        </svg>
        <div className="dash-donut-legend">
          {segments.map((seg, i) => (
            <div key={i} className="dash-donut-legend-item">
              <span className="dash-donut-dot" style={{ background: seg.color }} />
              {seg.label}
              <span className="dash-donut-legend-pct">—</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  let offset = 0;
  const arcs = segments.map(seg => {
    const pct = (seg.value || 0) / total;
    const dashLen = pct * circumference;
    const arc = { ...seg, dashLen, dashOffset: circumference - offset, pct: Math.round(pct * 100) };
    offset += dashLen;
    return arc;
  });

  return (
    <div className="dash-donut-wrap">
      <svg width="80" height="80" viewBox="0 0 80 80" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        {arcs.map((arc, i) => arc.dashLen > 0 && (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={arc.color} strokeWidth={stroke}
            strokeDasharray={`${arc.dashLen} ${circumference - arc.dashLen}`}
            strokeDashoffset={arc.dashOffset}
            strokeLinecap="butt"
            style={{ transform: "rotate(-90deg)", transformOrigin: "40px 40px" }}
          />
        ))}
      </svg>
      <div className="dash-donut-legend">
        {arcs.map((arc, i) => (
          <div key={i} className="dash-donut-legend-item">
            <span className="dash-donut-dot" style={{ background: arc.color }} />
            {arc.label}
            <span className="dash-donut-legend-pct">{arc.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── FilterBar component ──────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDate = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;

function FilterBar({ filters, onFiltersChange, topics, terms, TIME_PRESETS, onRunAll }) {
  const [openPanel, setOpenPanel] = useState(null); // "config" | "engines" | "topics" | "terms" | null

  const ENGINES = [
    { key: "openai",     label: "ChatGPT"   },
    { key: "claude",     label: "Claude"    },
    { key: "gemini",     label: "Gemini"    },
    { key: "perplexity", label: "Perplexity"},
    { key: "copilot",    label: "Copilot"   },
  ];

  const togglePanel = (name) => setOpenPanel(p => p === name ? null : name);

  // Close any open panel on outside click
  useEffect(() => {
    if (!openPanel) return;
    const handler = (e) => {
      if (!e.target.closest(".filter-multiselect-wrap") && !e.target.closest(".filter-config-wrap")) {
        setOpenPanel(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openPanel]);

  const canGoFwd = filters.timeOffset > 0;

  const goBack = () => onFiltersChange({ ...filters, timeOffset: filters.timeOffset + 1 });
  const goFwd  = () => onFiltersChange({ ...filters, timeOffset: Math.max(0, filters.timeOffset - 1) });

  // ── Picker label ──
  const preset = TIME_PRESETS[filters.timePresetIndex];
  const periodMs = ((preset?.hours ?? 0) * 3600_000) + ((preset?.days ?? 0) * 86400_000);
  let pickerLabel, pickerBadge;
  if (filters.timeOffset === 0) {
    pickerLabel = `Last ${preset?.label || "?"}`;
    pickerBadge = null;
  } else {
    const now = Date.now();
    const endTs   = now - filters.timeOffset * periodMs;
    const startTs = endTs - periodMs;
    pickerLabel = `${fmtDate(new Date(startTs))} – ${fmtDate(new Date(endTs))}`;
    pickerBadge = `-${filters.timeOffset}`;
  }

  // ── Topic/term helpers ──
  const handleTopicChange = (newTopicIds) => {
    const availableTermIds = new Set(
      terms.filter(t => newTopicIds.length === 0 || newTopicIds.includes(String(t.topic_id))).map(t => String(t.id))
    );
    onFiltersChange({ ...filters, topicIds: newTopicIds, termIds: filters.termIds.filter(id => availableTermIds.has(id)) });
  };
  const toggleEngine = (key) => {
    const next = filters.engines.includes(key) ? filters.engines.filter(e => e !== key) : [...filters.engines, key];
    onFiltersChange({ ...filters, engines: next });
  };
  const toggleTopic = (id) => {
    const sid = String(id);
    const next = filters.topicIds.includes(sid) ? filters.topicIds.filter(x => x !== sid) : [...filters.topicIds, sid];
    handleTopicChange(next);
  };
  const toggleTerm = (id) => {
    const sid = String(id);
    const next = filters.termIds.includes(sid) ? filters.termIds.filter(x => x !== sid) : [...filters.termIds, sid];
    onFiltersChange({ ...filters, termIds: next });
  };

  const visibleTerms = filters.topicIds.length > 0
    ? terms.filter(t => filters.topicIds.includes(String(t.topic_id)))
    : terms;

  const isDefault = filters.timePresetIndex === 2 && filters.timeOffset === 0 &&
    filters.engines.length === 0 && filters.topicIds.length === 0 && filters.termIds.length === 0;
  const DEFAULT_FILTERS = { timePresetIndex: 2, timeOffset: 0, engines: [], topicIds: [], termIds: [] };

  return (
    <div className="filter-bar">
      {/* ── Left: time navigation ── */}
      <div className="filter-time-group">
        <button className="filter-nav-btn" onClick={goBack}>‹</button>

        {/* Clickable time label opens dropdown */}
        <div className="filter-config-wrap" style={{ flex: 1 }}>
          <button
            className={`filter-time-label-btn${openPanel === "config" ? " active" : ""}`}
            onClick={() => togglePanel("config")}
            style={{ width: "100%", textAlign: "center" }}
          >
            {pickerLabel}
            {pickerBadge && <span className="filter-time-badge">{pickerBadge}</span>}
            <span style={{ marginLeft: 6, opacity: 0.5 }}>▾</span>
          </button>
          {openPanel === "config" && (
            <div className="filter-config-popup">
              <div className="filter-config-section-label">Timeframe</div>
              <div className="filter-preset-grid">
                {TIME_PRESETS.map((p, i) => (
                  <button
                    key={i}
                    className={`filter-preset-btn${filters.timePresetIndex === i ? " active" : ""}`}
                    onClick={() => { onFiltersChange({ ...filters, timePresetIndex: i, timeOffset: 0 }); togglePanel(null); }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button className="filter-nav-btn" disabled={!canGoFwd} onClick={goFwd}>›</button>
      </div>

      {/* ── Right: dimension filters ── */}
      <div className="filter-right-group">
        {/* Engines */}
        <div className="filter-multiselect-wrap">
          <button className={`filter-multiselect-btn${filters.engines.length > 0 ? " active" : ""}`} onClick={() => togglePanel("engines")}>
            Engines{filters.engines.length > 0 && ` · ${filters.engines.length}`}
            <span style={{ marginLeft: 5, opacity: 0.5 }}>▾</span>
          </button>
          {openPanel === "engines" && (
            <div className="filter-dropdown-panel">
              {ENGINES.map(e => (
                <label key={e.key} className="filter-dropdown-item">
                  <input type="checkbox" checked={filters.engines.includes(e.key)} onChange={() => toggleEngine(e.key)} />
                  {e.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Topics */}
        <div className="filter-multiselect-wrap">
          <button className={`filter-multiselect-btn${filters.topicIds.length > 0 ? " active" : ""}`} onClick={() => togglePanel("topics")}>
            Topics{filters.topicIds.length > 0 && ` · ${filters.topicIds.length}`}
            <span style={{ marginLeft: 5, opacity: 0.5 }}>▾</span>
          </button>
          {openPanel === "topics" && (
            <div className="filter-dropdown-panel">
              {topics.map(t => (
                <label key={t.id} className="filter-dropdown-item">
                  <input type="checkbox" checked={filters.topicIds.includes(String(t.id))} onChange={() => toggleTopic(t.id)} />
                  {t.name}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Queries */}
        <div className="filter-multiselect-wrap">
          <button className={`filter-multiselect-btn${filters.termIds.length > 0 ? " active" : ""}`} onClick={() => togglePanel("terms")}>
            Queries{filters.termIds.length > 0 && ` · ${filters.termIds.length}`}
            <span style={{ marginLeft: 5, opacity: 0.5 }}>▾</span>
          </button>
          {openPanel === "terms" && (
            <div className="filter-dropdown-panel">
              {visibleTerms.map(t => (
                <label key={t.id} className="filter-dropdown-item">
                  <input type="checkbox" checked={filters.termIds.includes(String(t.id))} onChange={() => toggleTerm(t.id)} />
                  {t.term}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Update visibility button */}
        {onRunAll && (
          <button
            className="dash-run-all-btn"
            style={{ marginLeft: 12 }}
            onClick={onRunAll}
          >
            ▶ Update Page-Audit und KI-Sichtbarkeit
          </button>
        )}
      </div>
    </div>
  );
}


function BrandFaviconAvatar({ websiteUrl, name, size = 44, fontSize = 18, borderRadius = 10 }) {
  const [imgOk, setImgOk] = React.useState(true);
  const domain = React.useMemo(() => {
    try { return new URL(websiteUrl).hostname; } catch { return null; }
  }, [websiteUrl]);
  const letter = (name?.[0] || "?").toUpperCase();
  const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null;

  if (faviconUrl && imgOk) {
    return (
      <div style={{ width: size, height: size, borderRadius, border: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
        <img src={faviconUrl} alt="" width={size * 0.6} height={size * 0.6} onError={() => setImgOk(false)} style={{ objectFit: "contain" }} />
      </div>
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius, background: "linear-gradient(135deg,#22c55e,#16a34a)", display: "flex", alignItems: "center", justifyContent: "center", fontSize, fontWeight: 800, color: "white", flexShrink: 0, boxShadow: "0 2px 8px rgba(34,197,94,0.3)" }}>
      {letter}
    </div>
  );
}

export default function BrandDashboard({ brand, onEdit, onDelete, autoOpenBrandId = null, onAutoOpenConsumed }) {
  const { t, lang } = useLanguage();
  const { refreshProfile } = useUser() || {};
  const [topics, setTopics]   = useState([]);
  const [terms, setTerms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [runningTopic, setRunningTopic] = useState(null);
  const [kpiData, setKpiData] = useState(null);
  const [competitorData, setCompetitorData] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [detailsOpen, setDetailsOpen] = useState(null); // "mention" | "sentiment" | "citations" | null
  const [detailsData, setDetailsData] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [discoveryRows, setDiscoveryRows] = useState([]);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [prevAuditData, setPrevAuditData] = useState(null);
  const [actionStatuses, setActionStatuses] = useState({}); // key: action index → status string
  const [actionSort, setActionSort] = useState({ col: "wirkung", dir: "desc" });

  const TIME_PRESETS = [
    { label: "24 Hours", hours: 24  },
    { label: "7 Days",   days: 7    },
    { label: "30 Days",  days: 30   },
    { label: "3 Months", days: 90   },
    { label: "1 Year",   days: 365  },
  ];
  const [filters, setFilters] = useState({ timePresetIndex: 2, timeOffset: 0, engines: [], topicIds: [], termIds: [] });
  const [runs, setRuns] = useState([]);

  const brandId = brand?.id ?? null;

  // Auto-open run modal after wizard completes — only fires when this dashboard's brand matches the target
  useEffect(() => {
    if (autoOpenBrandId === brandId && brandId && !loading) {
      // Reload fresh data first (wizard may have deleted & recreated topics/terms with new IDs)
      const reloadAndOpen = async () => {
        setLoading(true);
        try {
          const topicsResult = await supabase
            .from("topics").select("*").eq("brand_id", brandId).order("created_at");
          const loadedTopics = topicsResult.data || [];
          setTopics(loadedTopics);

          let loadedTerms = [];
          if (loadedTopics.length > 0) {
            const topicIds = loadedTopics.map(t => t.id);
            const termsResult = await supabase
              .from("search_terms").select("*").in("topic_id", topicIds);
            loadedTerms = termsResult.data || [];
            setTerms(loadedTerms);
          }

          if (loadedTopics.length > 0) {
            const byTopic = {};
            loadedTopics.forEach(tp => {
              byTopic[tp.id] = loadedTerms.filter(tm => tm.topic_id === tp.id);
            });
            setRunningTopic({ mode: "multi", allTopics: loadedTopics, allTermsByTopic: byTopic });
          }
          // Always consume so App.js clears the target — prevents stale trigger on brand switch
          if (onAutoOpenConsumed) onAutoOpenConsumed();
        } catch (err) {
          console.error("Auto-open reload error:", err);
        } finally {
          setLoading(false);
        }
      };
      reloadAndOpen();
    }
  }, [autoOpenBrandId, brandId, loading]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const brandIdToDelete = brandId; // Capture before state changes
      
      // Delete brand (CASCADE should delete topics and terms)
      const { error } = await supabase.from("brands").delete().eq("id", brandIdToDelete);
      
      if (error) {
        console.error("Delete error:", error);
        alert("Fehler beim Löschen der Marke: " + error.message);
        setDeleting(false);
        setShowDeleteConfirm(false);
        return;
      }
      
      // Success - close modal immediately and notify parent
      setShowDeleteConfirm(false);
      setDeleting(false);
      
      // Track in HubSpot
      try {
        await apiFetch("/hubspot/brand-event", {
          method: "POST",
          body: JSON.stringify({
            event: "brand_deleted",
            userId: brand.user_id,
            brandId: brandIdToDelete
          })
        });
      } catch (hsErr) {
        console.warn("HubSpot tracking failed:", hsErr);
      }
      
      // Notify parent to refresh brands list (will unmount this component)
      if (onDelete) onDelete(brandIdToDelete);
      
    } catch (err) {
      console.error("Delete error:", err);
      alert("Unerwarteter Fehler beim Löschen.");
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  useEffect(() => {
    // Reset on brand change
    setTopics([]);
    setTerms([]);
    setDetailsOpen(null);
    setDetailsData([]);
    setAuditData(null);
    setPrevAuditData(null);

    if (!brandId) {
      setLoading(false);
      return;
    }

    loadData();
    loadRuns();
  }, [brandId]);

  const loadData = async () => {
      setLoading(true);
      try {
        // 1. Load topics for this brand
        const topicsResult = await supabase
          .from("topics")
          .select("*")
          .eq("brand_id", brandId)
          .order("created_at");

        console.log("Topics loaded for brandId:", brandId, topicsResult.data, "Error:", topicsResult.error);
        if (topicsResult.error) console.error("Topics error:", topicsResult.error);

        const loadedTopics = topicsResult.data || [];
        setTopics(loadedTopics);

        // 2. Load terms via topic_ids (search_terms has no brand_id column)
        if (loadedTopics.length > 0) {
          const topicIds = loadedTopics.map(t => t.id);
          const termsResult = await supabase
            .from("search_terms")
            .select("*")
            .in("topic_id", topicIds);

          console.log("Terms loaded:", termsResult.data, "Error:", termsResult.error);
          if (termsResult.error) console.error("Terms error:", termsResult.error);
          setTerms(termsResult.data || []);
        } else {
          setTerms([]);
        }
      } catch (err) {
        console.error("Dashboard load error:", err);
        setTopics([]);
        setTerms([]);
      } finally {
        setLoading(false);
      }
  };

  const getTermsForTopic = (topicId) =>
    terms.filter(t => t.topic_id === topicId);

  const buildFilterParams = (f) => {
    const params = new URLSearchParams({ brandId });
    const preset = TIME_PRESETS[f.timePresetIndex];
    if (preset) {
      const periodMs = ((preset.hours ?? 0) * 3600_000) + ((preset.days ?? 0) * 86400_000);
      const now = Date.now();
      const endTs   = now - f.timeOffset * periodMs;
      const startTs = endTs - periodMs;
      params.set("startDate", new Date(startTs).toISOString());
      if (f.timeOffset > 0) params.set("endDate", new Date(endTs).toISOString());
    }
    if (f.engines.length > 0)  params.set("engines",  f.engines.join(","));
    if (f.topicIds.length > 0) params.set("topicIds", f.topicIds.join(","));
    if (f.termIds.length > 0)  params.set("termIds",  f.termIds.join(","));
    return params.toString();
  };

  const loadKPIs = async (activeFilters = filters) => {
    if (!brandId) return;
    try {
      const res = await apiFetch(`/kpis?${buildFilterParams(activeFilters)}`);
      const data = await res.json();
      if (!data.error) setKpiData(data);
    } catch (err) {
      console.error("KPI load error:", err);
    }
  };

  const loadCompetitors = async (activeFilters = filters) => {
    if (!brandId) return;
    try {
      const res = await apiFetch(`/competitors?${buildFilterParams(activeFilters)}`);
      const data = await res.json();
      if (!data.error) setCompetitorData(data);
    } catch (err) {
      console.error("Competitor load error:", err);
    }
  };

  const loadDiscovery = async () => {
    if (!brandId) return;
    try {
      const res = await apiFetch(`/citation-discovery?brandId=${brandId}`);
      const data = await res.json();
      setDiscoveryRows(data.rows || []);
    } catch (err) { console.error("Discovery load error:", err); }
  };

  const loadRuns = async () => {
    if (!brandId) return;
    try {
      const res = await apiFetch(`/runs?brandId=${brandId}`);
      const data = await res.json();
      setRuns(data.runs || []);
    } catch (err) {
      console.error("Runs load error:", err);
    }
  };

  const handleDiscoveryToggle = async (topicId, enabled) => {
    try {
      await apiFetch(`/topics/${topicId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ discovery_enabled: enabled }) });
      setTopics(prev => prev.map(t => t.id === topicId ? { ...t, discovery_enabled: enabled } : t));
    } catch (e) { console.error("[discovery-toggle] Error:", e.message); }
  };

  const loadQualityDetails = async (qualityType) => {
    if (detailsOpen === qualityType) { setDetailsOpen(null); return; }
    setDetailsOpen(qualityType);
    setDetailsLoading(true);
    try {
      const res = await apiFetch(`/quality-details?brandId=${brandId}&qualityType=${qualityType}`);
      const data = await res.json();
      setDetailsData(data.rows || []);
    } catch (err) {
      console.error("Quality details error:", err);
      setDetailsData([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Load KPIs + competitors when brandId or filters change
  useEffect(() => {
    if (brandId) { loadKPIs(filters); loadCompetitors(filters); }
  }, [brandId, filters]);

  // Load discovery results once on mount (not filter-dependent — always latest run)
  useEffect(() => {
    if (brandId) loadDiscovery();
  }, [brandId]);

  const loadAudit = async () => {
    if (!brandId) return;
    try {
      const FIELDS = "url, overall_score, summary, content_change_summary, content_improvements, categories, structural, created_at";
      // Fetch 2 most recent audits by brand_id
      const { data: byId } = await supabase
        .from("page_audits")
        .select(FIELDS)
        .eq("brand_id", brandId)
        .order("created_at", { ascending: false })
        .limit(2);
      if (byId?.length) {
        setAuditData(byId[0]);
        setPrevAuditData(byId[1] || null);
        return;
      }
      // Fallback: match by website_url (older records without brand_id)
      if (brand?.website_url) {
        const { data: byUrl } = await supabase
          .from("page_audits")
          .select(FIELDS)
          .eq("url", brand.website_url)
          .order("created_at", { ascending: false })
          .limit(2);
        if (byUrl?.length) {
          setAuditData(byUrl[0]);
          setPrevAuditData(byUrl[1] || null);
        }
      }
    } catch (err) { console.error("Audit load error:", err); }
  };

  const loadActionStatuses = async () => {
    if (!brandId) return;
    try {
      const { data } = await supabase
        .from("audit_action_statuses")
        .select("action_key, status")
        .eq("brand_id", brandId);
      if (data) {
        const map = {};
        data.forEach(row => { map[row.action_key] = row.status; });
        setActionStatuses(map);
      }
    } catch (err) { console.error("Action status load error:", err); }
  };

  const updateActionStatus = async (actionKey, newStatus) => {
    setActionStatuses(prev => ({ ...prev, [actionKey]: newStatus }));
    try {
      await supabase.from("audit_action_statuses").upsert(
        { brand_id: brandId, action_key: actionKey, status: newStatus, updated_at: new Date().toISOString() },
        { onConflict: "brand_id,action_key" }
      );
    } catch (err) { console.error("Action status update error:", err); }
  };

  useEffect(() => {
    if (brandId) { loadAudit(); loadActionStatuses(); }
  }, [brandId]);

  // Get KPIs — real data or null placeholders
  const globalKPIs = kpiData?.globalKPIs || { visibility: null, ranking: null, mentions: null, sentiment: null };
  // Helper: extract per-KPI sparkline history with correct value field
  const kpiH = (field) => (globalKPIs.kpiHistory || [])
    .map(h => ({ date: h.date, visibility: h[field] }))
    .filter(h => h.visibility !== null);

  const getTopicKPIs = (topicId) => {
    const topicData = kpiData?.topics?.find(t => t.id === topicId);
    return topicData?.kpis || { visibility: null, ranking: null, mentions: null, sentiment: null };
  };

  const getTopicCompetitors = (topicName) => {
    const topicData = competitorData?.topics?.find(t => t.name === topicName);
    if (!topicData) return [];
    const latest = topicData.history?.[topicData.history.length - 1];
    const prev   = topicData.history?.[topicData.history.length - 2];
    return (topicData.competitors || []).map(comp => {
      const latestVal = latest?.brands?.[comp.name]?.visibility ?? comp.visibility;
      const prevVal   = prev?.brands?.[comp.name]?.visibility ?? null;
      return { ...comp, visibility: latestVal, delta: prevVal != null ? latestVal - prevVal : null };
    });
  };

  if (loading) {
    return (
      <div className="dash-root">
        <style>{css}</style>
        <div className="dash-loading">
          <div className="dash-spinner" />
          {t("dash.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="dash-root">
      <style>{css}</style>

      {/* ── Brand Header ── */}
      <div className="dash-brand-header">
        <div className="dash-brand-left">
          <BrandFaviconAvatar websiteUrl={brand.website_url} name={brand.primary_name} size={44} fontSize={18} borderRadius={10} />
          <div>
            <div className="dash-brand-name">{brand.primary_name}</div>
            <div className="dash-brand-url">
              {brand.website_url}
              {brand.industry && (
                <span style={{
                  marginLeft: 8,
                  padding: "2px 8px",
                  background: "#f0f7ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 100,
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#2563eb",
                }}>
                  {brand.industry}
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="dash-edit-btn" onClick={() => onEdit && onEdit(brand)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5}}>
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            {t("dash.editBrand")}
          </button>
          <button
            className="dash-edit-btn"
            onClick={() => setShowDeleteConfirm(true)}
            style={{borderColor:"#fca5a5",color:"#dc2626"}}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:5}}>
              <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            {t("dash.deleteBrand")}
          </button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        topics={topics}
        terms={terms}
        TIME_PRESETS={TIME_PRESETS}
        onRunAll={() => {
          const byTopic = {};
          topics.forEach(tp => { byTopic[tp.id] = terms.filter(tm => tm.topic_id === tp.id); });
          setRunningTopic({ mode: "multi", allTopics: topics, allTermsByTopic: byTopic });
        }}
      />

      {/* ── Tab Navigation ── */}
      <div className="dash-tabs">
        <button className={`dash-tab ${activeTab === "dashboard" ? "active" : ""}`}
          onClick={() => setActiveTab("dashboard")}>
          {t("dashboard.tabDashboard")}
        </button>
        <button className={`dash-tab ${activeTab === "actions" ? "active" : ""}`}
          onClick={() => setActiveTab("actions")}>
          {t("dashboard.tabMassnahmen")}
        </button>
        <button className={`dash-tab ${activeTab === "keyPrompts" ? "active" : ""}`}
          onClick={() => setActiveTab("keyPrompts")}
          style={{ display: "flex", alignItems: "center", gap: 7 }}>
          Key Prompts
          <span style={{
            fontSize: 10, fontFamily: "DM Mono,monospace", letterSpacing: "0.06em",
            padding: "1px 7px", borderRadius: 3,
            background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
            whiteSpace: "nowrap",
          }}>{t("dashboard.badgeNeu")}</span>
        </button>
      </div>

      {/* ── Dashboard Tab ── */}
      {activeTab === "dashboard" && (() => {
        const mq = globalKPIs.mentionQuality   || { empfohlen: 0, erklärt: 0, erwähnt: 0 };
        const sq = globalKPIs.sentimentQuality || { positiv: 0, neutral: 0, negativ: 0 };
        const cq = globalKPIs.citationsQuality || { eigene_domain: 0, drittanbieter: 0, andere: 0 };
        const pp = globalKPIs.perProvider      || {};

        const visColor = globalKPIs.visibility == null ? "#94a3b8"
          : globalKPIs.visibility >= 66 ? "#16a34a"
          : globalKPIs.visibility >= 33 ? "#f59e0b" : "#dc2626";

        // Aggregate all competitors across topics
        const allCompetitors = (() => {
          if (!competitorData?.topics?.length) return { ownBrand: null, list: [] };
          const compMap = new Map();
          let ownBrand = null;
          for (const topic of competitorData.topics) {
            if (topic.ownBrand && !ownBrand) ownBrand = { ...topic.ownBrand };
            for (const c of (topic.competitors || [])) {
              const key = c.name.toLowerCase();
              if (!compMap.has(key)) {
                compMap.set(key, { ...c });
              } else {
                const existing = compMap.get(key);
                existing.mentions   = (existing.mentions || 0) + (c.mentions || 0);
                existing.visibility = Math.max(existing.visibility || 0, c.visibility || 0);
                existing.citations  = (existing.citations || 0) + (c.citations || 0);
              }
            }
          }
          const list = [...compMap.values()].sort((a, b) => (b.visibility || 0) - (a.visibility || 0)).slice(0, 10);
          return { ownBrand, list };
        })();

        // Count total recommendations across all categories for the hero pill
        const openCount = auditData?.categories
          ? ["contentQuality","authority","technicalSeo","engagement"]
              .reduce((s, k) => s + (auditData.categories[k]?.recommendations?.length || 0), 0)
          : 0;

        return <>
          {/* ── Two Sides Hero ── */}
          <div className="ts-hero">
            {/* Side A — Website */}
            <div className="ts-side-a">
              <div className="ts-eyebrow">{t("dashboard.heroEyebrowA")}</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                <div className="ts-score-big">
                  {auditData ? auditData.overall_score : "—"}
                  {auditData && <span className="ts-score-denom">/100</span>}
                </div>
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="ts-score-title">{t("dashboard.heroPageAudit")}</div>
                    {auditData && (
                      <button
                        onClick={() => {
                          // window.open must happen synchronously in the click handler
                          // (before the async PDF render) or popup blockers may block it.
                          const tab = window.open("", "_blank");
                          openAuditPdf(auditData, "de", tab);
                        }}
                        style={{ fontSize: 11, color: "#1f3df5", background: "none", border: "1px solid #1f3df5", borderRadius: 4, padding: "1px 7px", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                      >
                        {t("dashboard.heroReportOpen")}
                      </button>
                    )}
                  </div>
                  <div className="ts-score-sub">{t("dashboard.heroInfluence")}</div>
                </div>
              </div>
              {openCount > 0 && (
                <span className="ts-open-pill" onClick={() => setActiveTab("actions")} style={{ cursor: "pointer" }}>
                  {openCount} {t("dashboard.heroActionsCount", { count: openCount })}
                </span>
              )}
              {!auditData && (
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
                  {t("dashboard.heroNoAudit")}
                </div>
              )}
            </div>

            {/* Bridge */}
            <div className="ts-bridge">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              <div className="ts-bridge-label">{t("dashboard.heroBridge")}</div>
            </div>

            {/* Side B — AI Visibility */}
            <div className="ts-side-b">
              <div className="ts-eyebrow" style={{ color: "#1f3df5" }}>{t("dashboard.heroEyebrowB")}</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                <div className="ts-score-big accent">
                  {globalKPIs.visibility != null ? globalKPIs.visibility : "—"}
                  {globalKPIs.visibility != null && <span className="ts-score-denom" style={{ color: "#94a3b8" }}>%</span>}
                </div>
                <div style={{ marginBottom: 6 }}>
                  <div className="ts-score-title">{t("dashboard.heroKiVisibility")}</div>
                  <div className="ts-score-sub">{t("dashboard.heroKiResult")}</div>
                </div>
              </div>
              {kpiH("visibility").length >= 2 && (
                <div style={{ marginTop: 12 }}>
                  <Sparkline data={kpiH("visibility")} color="#1f3df5" height={40} />
                </div>
              )}
            </div>
          </div>

          {/* ── Section 1: REICHWEITE ── */}
          <div className="dash-section">
            <div className="dash-section-header">
              <div>
                <div className="dash-section-label">{t("dashboard.sec1Label")}</div>
                <div className="dash-section-title">{t("dashboard.sec1Title")}</div>
                <div className="dash-section-subtitle">{t("dashboard.sec1Sub")}</div>
              </div>
            </div>
            <div className="dash-reach-grid">
              {/* Visibility */}
              <div className="dash-reach-card">
                <div className="dash-reach-label">{t("dashboard.kpiVisibility")}</div>
                <div className="dash-reach-value" style={{ color: visColor }}>
                  {globalKPIs.visibility != null ? `${globalKPIs.visibility}%` : "—"}
                </div>
                <div className="dash-reach-sub">{t("dashboard.kpiVisibilitySub")}</div>
                {kpiH("visibility").length >= 2 && <Sparkline data={kpiH("visibility")} color={visColor} height={36} />}
              </div>
              {/* Mentions */}
              <div className="dash-reach-card">
                <div className="dash-reach-label">{t("dashboard.kpiMentions")}</div>
                <div className="dash-reach-value" style={{ color: "#0f172a" }}>
                  {globalKPIs.mentions != null ? globalKPIs.mentions : "—"}
                </div>
                <div className="dash-reach-sub">{t("dashboard.kpiMentionsSub")}</div>
              </div>
              {/* Top 3 */}
              <div className="dash-reach-card">
                <div className="dash-reach-label">{t("dashboard.kpiTop3")}</div>
                <div className="dash-reach-value" style={{ color: globalKPIs.top3Visibility >= 30 ? "#16a34a" : "#f59e0b" }}>
                  {globalKPIs.top3Visibility != null ? `${globalKPIs.top3Visibility}%` : "—"}
                </div>
                <div className="dash-reach-sub">{t("dashboard.kpiTop3Sub")}</div>
              </div>
              {/* Detection Rate */}
              <div className="dash-reach-card">
                <div className="dash-reach-label">{t("dashboard.kpiDetection")}</div>
                <div className="dash-reach-value" style={{ color: globalKPIs.detectionRate >= 50 ? "#16a34a" : "#dc2626" }}>
                  {globalKPIs.detectionRate != null ? `${globalKPIs.detectionRate}%` : "—"}
                </div>
                <div className="dash-reach-sub">{t("dashboard.kpiDetectionSub")}</div>
              </div>
            </div>
          </div>

          {/* ── Section 2: QUALITÄT ── */}
          <div className="dash-section">
            <div className="dash-section-header">
              <div>
                <div className="dash-section-label">{t("dashboard.sec2Label")}</div>
                <div className="dash-section-title">{t("dashboard.sec2Title")}</div>
                <div className="dash-section-subtitle">{t("dashboard.sec2Sub")}</div>
              </div>
            </div>
            <div className="dash-quality-grid">
              {/* Sentiment donut */}
              <div className="dash-quality-card">
                <div className="dash-quality-label">{t("dashboard.qualSentiment")}</div>
                <DonutChart segments={[
                  { label: "Positiv",  value: sq.positiv,  color: "#16a34a" },
                  { label: "Neutral",  value: sq.neutral,  color: "#f59e0b" },
                  { label: "Negativ",  value: sq.negativ,  color: "#dc2626" },
                ]} />
                <button className="dash-details-link" onClick={() => loadQualityDetails("sentiment")}>
                  {detailsOpen === "sentiment" ? "▲ schliessen" : "▼ details"}
                </button>
              </div>
              {/* Mentions quality donut */}
              <div className="dash-quality-card">
                <div className="dash-quality-label">{t("dashboard.qualMention")}</div>
                <DonutChart segments={[
                  { label: "Empfohlen", value: mq.empfohlen, color: "#16a34a" },
                  { label: "Erklärt",   value: mq["erklärt"] || 0, color: "#2563eb" },
                  { label: "Erwähnt",   value: mq["erwähnt"] || 0, color: "#94a3b8" },
                ]} />
                <button className="dash-details-link" onClick={() => loadQualityDetails("mention")}>
                  {detailsOpen === "mention" ? "▲ schliessen" : "▼ details"}
                </button>
              </div>
              {/* Ø Position */}
              <div className="dash-quality-card">
                <div className="dash-quality-label">{t("dashboard.qualPosition")}</div>
                <div style={{ fontSize: 42, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                  {globalKPIs.ranking != null ? `#${globalKPIs.ranking}` : "—"}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>{t("dashboard.qualPositionSub")}</div>
                <div style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: globalKPIs.top3Visibility >= 30 ? "#16a34a" : "#f59e0b" }}>
                  {globalKPIs.top3Visibility != null ? `Top 3: ${globalKPIs.top3Visibility}%` : ""}
                </div>
              </div>
              {/* Citations count */}
              <div className="dash-quality-card">
                <div className="dash-quality-label">{t("dashboard.qualCitations")}</div>
                <div style={{ fontSize: 42, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                  {(() => { const cq = globalKPIs.citationsQuality; if (!cq) return "—"; const total = (cq.eigene_domain || 0) + (cq.externe_quellen || 0) + (cq.social || 0); return total > 0 ? total : "—"; })()}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>{t("dashboard.qualCitationsSub")}</div>
              </div>
            </div>

            {/* Details panel */}
            {(detailsOpen === "sentiment" || detailsOpen === "mention") && (
              <div className="dash-details-panel" style={{ marginTop: 12 }}>
                {detailsLoading ? (
                  <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Laden…</div>
                ) : (
                  <table className="dash-details-table">
                    <thead>
                      <tr>
                        <th>Engine</th>
                        <th>Suchanfrage</th>
                        <th>Kategorie</th>
                        <th>Textauszug</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsData.length === 0 ? (
                        <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8" }}>Keine Daten (noch keine Runs mit neuer Auswertung)</td></tr>
                      ) : detailsData.map((row, i) => (
                        <tr key={i}>
                          <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{row.provider}</td>
                          <td>{row.term}</td>
                          <td>
                            <span className={`dash-tier-badge dash-tier-${row.tier}`}>{row.tier}</span>
                          </td>
                          <td style={{ color: "#64748b", fontStyle: "italic", fontSize: 11 }}>{row.snippet || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* ── Section 3: QUELLEN ── */}
          <div className="dash-section">
            <div className="dash-section-header">
              <div>
                <div className="dash-section-label">{t("dashboard.sec3Label")}</div>
                <div className="dash-section-title">{t("dashboard.sec3Title")}</div>
                <div className="dash-section-subtitle">{t("dashboard.sec3Sub")}</div>
              </div>
            </div>
            <div className="dash-sources-grid">
              {/* AI Engine performance */}
              <div className="dash-quality-card">
                <div className="dash-quality-label">{t("dashboard.srcEnginePerf")}</div>
                {[
                  { key: "openai",     label: "ChatGPT",   color: "#10b981" },
                  { key: "perplexity", label: "Perplexity",color: "#2563eb" },
                  { key: "claude",     label: "Claude",    color: "#7c3aed" },
                  { key: "gemini",     label: "Gemini",    color: "#f59e0b" },
                  { key: "copilot",    label: "Copilot",   color: "#0078d4" },
                ].map(e => (
                  <div key={e.key} className="dash-engine-row">
                    <span className="dash-engine-name">{e.label}</span>
                    <div className="dash-engine-bar-wrap">
                      <div className="dash-engine-bar-fill" style={{ width: `${pp[e.key] || 0}%`, background: e.color }} />
                    </div>
                    <span className="dash-engine-pct">{pp[e.key] != null ? `${pp[e.key]}%` : "—"}</span>
                  </div>
                ))}
              </div>
              {/* Citation sources donut */}
              <div className="dash-quality-card">
                <div className="dash-quality-label">{t("dashboard.srcCitation")}</div>
                <DonutChart segments={[
                  { label: "Externe Quellen", value: cq.externe_quellen || 0, color: "#10b981" },
                  { label: "Social",          value: cq.social || 0,          color: "#7c3aed" },
                  { label: "Eigene Domain",   value: cq.eigene_domain || 0,   color: "#2563eb" },
                ]} />
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6, lineHeight: 1.4 }}>
                  {t("dashboard.srcNote")}
                </div>
                <button className="dash-details-link" onClick={() => loadQualityDetails("citations")}>
                  {detailsOpen === "citations" ? "▲ schliessen" : "▼ details"}
                </button>
              </div>
            </div>

            {/* Citations details panel — full width below the grid */}
            {detailsOpen === "citations" && (
              <div className="dash-details-panel" style={{ marginTop: 12 }}>
                {detailsLoading ? (
                  <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Laden…</div>
                ) : (
                  <table className="dash-details-table">
                    <thead><tr><th>Engine</th><th>Suchanfrage</th><th>Kategorie</th><th>URL</th></tr></thead>
                    <tbody>
                      {detailsData.length === 0 ? (
                        <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8" }}>Keine Daten (noch keine Runs mit neuer Auswertung)</td></tr>
                      ) : detailsData.flatMap((row, i) => {
                        const cats = [
                          { key: "externe_quellen", label: "Externe Quellen", color: "#10b981" },
                          { key: "social",          label: "Social",          color: "#7c3aed" },
                          { key: "eigene_domain",   label: "Eigene Domain",   color: "#2563eb" },
                        ];
                        const entries = cats.flatMap(cat =>
                          (row[cat.key] || []).map(url => ({ cat, url }))
                        );
                        if (!entries.length) return [
                          <tr key={`${i}-empty`}>
                            <td style={{ whiteSpace: "nowrap", fontWeight: 500 }}>{row.provider}</td>
                            <td>{row.term}</td>
                            <td colSpan={2} style={{ color: "#94a3b8" }}>—</td>
                          </tr>
                        ];
                        return entries.map(({ cat, url }, j) => (
                          <tr key={`${i}-${j}`}>
                            {j === 0 && <td style={{ whiteSpace: "nowrap", fontWeight: 500 }} rowSpan={entries.length}>{row.provider}</td>}
                            {j === 0 && <td rowSpan={entries.length}>{row.term}</td>}
                            <td><span className="dash-tier-badge" style={{ background: cat.color + "22", color: cat.color, border: `1px solid ${cat.color}55` }}>{cat.label}</span></td>
                            <td style={{ fontSize: 11, color: "#475569", wordBreak: "break-all" }}>
                              {url.startsWith("http") ? <a href={url} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>{url}</a> : url}
                            </td>
                          </tr>
                        ));
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* ── Citation Discovery section ── */}
          {topics.length > 0 && (
            <div className="dash-section" style={{ paddingTop: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8", marginBottom: 4 }}>
                    3rd-party Citation Discovery
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>
                    Actively probes each AI engine for sources that mention your brand. Enable per topic below.
                  </div>
                </div>
                {discoveryRows.length > 0 && (
                  <button className="dash-details-link" onClick={() => setDiscoveryOpen(o => !o)}>
                    {discoveryOpen ? "▲ schliessen" : `▼ ${discoveryRows.reduce((s, r) => s + r.citations.length, 0)} Quellen anzeigen`}
                  </button>
                )}
              </div>

              {/* Topic toggles */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                {topics.map(tp => (
                  <button
                    key={tp.id}
                    className={`dash-topic-btn${tp.discovery_enabled ? " active" : ""}`}
                    onClick={() => handleDiscoveryToggle(tp.id, !tp.discovery_enabled)}
                    title={tp.discovery_enabled ? "Click to disable" : "Click to enable (≈doubles AI cost for this topic)"}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    {tp.name}
                    <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.7 }}>{tp.discovery_enabled ? "ON" : "OFF"}</span>
                  </button>
                ))}
              </div>

              {/* Discovery results table */}
              {discoveryOpen && discoveryRows.length > 0 && (
                <div className="dash-details-panel" style={{ marginTop: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#64748b", fontWeight: 600, fontSize: 11 }}>Engine</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#64748b", fontWeight: 600, fontSize: 11 }}>Topic / Query</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#64748b", fontWeight: 600, fontSize: 11 }}>Discovered URL</th>
                        <th style={{ textAlign: "left", padding: "6px 8px", color: "#64748b", fontWeight: 600, fontSize: 11 }}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discoveryRows.flatMap((row, ri) =>
                        row.citations.map((c, ci) => (
                          <tr key={`${ri}-${ci}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "6px 8px", color: "#64748b" }}>{row.provider}</td>
                            <td style={{ padding: "6px 8px", color: "#64748b" }}>{row.topic || row.term}</td>
                            <td style={{ padding: "6px 8px" }}>
                              <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", wordBreak: "break-all" }}>{c.url}</a>
                            </td>
                            <td style={{ padding: "6px 8px", color: "#475569" }}>{c.description}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {discoveryRows.length === 0 && topics.some(tp => tp.discovery_enabled) && (
                <div style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                  No discovery results yet — run an update for a topic with discovery enabled.
                </div>
              )}
            </div>
          )}

          {/* ── Section 4: WETTBEWERB ── */}
          <div className="dash-section">
            <div className="dash-section-header">
              <div>
                <div className="dash-section-label">{t("dashboard.sec4Label")}</div>
                <div className="dash-section-title">{t("dashboard.sec4Title")}</div>
                <div className="dash-section-subtitle">{t("dashboard.sec4Sub")}</div>
              </div>
            </div>
            {!allCompetitors.ownBrand && !allCompetitors.list.length ? (
              <div className="dash-no-data-section">Noch keine Mitbewerber-Analyse. Starte einen Run um Daten zu laden.</div>
            ) : (() => {
              const maxVis = Math.max(
                allCompetitors.ownBrand?.visibility || 0,
                ...allCompetitors.list.map(c => c.visibility || 0),
                1
              );
              return (
                <table className="dash-comp-section-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t("dashboard.thBrand")}</th>
                      <th>{t("dashboard.thVisibility")}</th>
                      <th>{t("dashboard.thPosition")}</th>
                      <th>{t("dashboard.thTop3")}</th>
                      <th>{t("dashboard.thSentiment")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCompetitors.ownBrand && (() => {
                      const ob = allCompetitors.ownBrand;
                      return (
                        <tr className="own-brand-row">
                          <td><span className="dash-own-star">★</span></td>
                          <td>{ob.name || brand.primary_name}</td>
                          <td>
                            <div className="dash-vis-bar-wrap">
                              <div className="dash-vis-bar-track">
                                <div className="dash-vis-bar-fill" style={{ width: `${Math.round((ob.visibility || 0) / maxVis * 100)}%`, background: "#2563eb" }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{ob.visibility != null ? `${ob.visibility}%` : "—"}</span>
                            </div>
                          </td>
                          <td>{ob.avgPosition != null ? `#${ob.avgPosition}` : "—"}</td>
                          <td>{ob.top3Visibility != null ? `${ob.top3Visibility}%` : "—"}</td>
                          <td style={{ color: (ob.sentiment || 50) >= 60 ? "#16a34a" : (ob.sentiment || 50) <= 40 ? "#dc2626" : "#f59e0b" }}>
                            {ob.sentiment != null ? `${ob.sentiment > 50 ? "+" : ""}${ob.sentiment - 50}` : "—"}
                          </td>
                        </tr>
                      );
                    })()}
                    {allCompetitors.list.map((c, i) => (
                      <tr key={c.name}>
                        <td style={{ color: "#94a3b8", fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{c.name}</td>
                        <td>
                          <div className="dash-vis-bar-wrap">
                            <div className="dash-vis-bar-track">
                              <div className="dash-vis-bar-fill" style={{ width: `${Math.round((c.visibility || 0) / maxVis * 100)}%`, background: "#94a3b8" }} />
                            </div>
                            <span style={{ fontSize: 12 }}>{c.visibility != null ? `${c.visibility}%` : "—"}</span>
                          </div>
                        </td>
                        <td>{c.avgPosition != null ? `#${c.avgPosition}` : "—"}</td>
                        <td>{c.top3Visibility != null ? `${c.top3Visibility}%` : "—"}</td>
                        <td style={{ color: (c.sentiment || 50) >= 60 ? "#16a34a" : (c.sentiment || 50) <= 40 ? "#dc2626" : "#f59e0b" }}>
                          {c.sentiment != null ? `${c.sentiment > 50 ? "+" : ""}${c.sentiment - 50}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>


          {/* Topic Run Modal */}
          {runningTopic && (
            <TopicRunModal
              topic={runningTopic.topic || null}
              terms={runningTopic.terms || []}
              brand={brand}
              mode={runningTopic.mode}
              onClose={() => setRunningTopic(null)}
              onRunComplete={() => {
                loadKPIs(filters); loadCompetitors(filters); loadRuns(); loadDiscovery();
                loadAudit(); loadActionStatuses();
                refreshProfile?.();
              }}
              allTopics={runningTopic.allTopics || null}
              allTermsByTopic={runningTopic.allTermsByTopic || null}
            />
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div style={{
              position:"fixed", inset:0, background:"rgba(0,0,0,0.4)",
              display:"flex", alignItems:"center", justifyContent:"center", zIndex:999
            }}>
              <div style={{
                background:"white", borderRadius:12, padding:24, width:440, maxWidth:"90vw",
                boxShadow:"0 20px 60px rgba(0,0,0,0.3)"
              }}>
                <h3 style={{margin:"0 0 12px", fontSize:18, fontWeight:700, color:"#0f172a"}}>
                  {t("dash.deleteTitle")}
                </h3>
                <p style={{margin:"0 0 20px", fontSize:14, color:"#64748b", lineHeight:1.5}}>
                  {t("dash.deleteConfirm")} <strong>{brand.primary_name}</strong>{t("dash.deleteConfirm2")}
                </p>
                <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleting}
                    style={{
                      padding:"8px 16px", borderRadius:7, border:"1.5px solid #e2e8f0",
                      background:"white", color:"#64748b", fontSize:13, fontWeight:500,
                      cursor:"pointer", fontFamily:"'new-science','DM Sans',sans-serif"
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    style={{
                      padding:"8px 16px", borderRadius:7, border:"none",
                      background:"#dc2626", color:"white", fontSize:13, fontWeight:600,
                      cursor:"pointer", fontFamily:"'new-science','DM Sans',sans-serif"
                    }}
                  >
                    {deleting ? t("dash.deleting") : t("dash.deleteBrand")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>;
      })()}

      {/* ── Actions Tab ── */}
      {activeTab === "actions" && (() => {
        const CAT_ORDER = ["contentQuality", "authority", "technicalSeo", "engagement"];
        const CAT_LABELS = {
          contentQuality: t("dashboard.catContentQuality"),
          authority:      t("dashboard.catAuthority"),
          technicalSeo:   t("dashboard.catTechnicalSeo"),
          engagement:     t("dashboard.catEngagement"),
        };
        const STATUS_OPTIONS = [
          { value: "open",        label: t("dashboard.statusOpen") },
          { value: "in_progress", label: t("dashboard.statusInProgress") },
          { value: "done",        label: t("dashboard.statusDone") },
          { value: "ignore",      label: t("dashboard.statusIgnore") },
        ];

        // Build set of previous audit recommendation titles for "Neu" detection
        const prevTitles = new Set();
        if (prevAuditData?.categories) {
          CAT_ORDER.forEach(catKey => {
            (prevAuditData.categories[catKey]?.recommendations || []).forEach(r => {
              const t = (typeof r === "object" ? r?.title : r) || "";
              if (t) prevTitles.add(t.trim().toLowerCase());
            });
          });
        }

        // Build flat list of all actions with category + index for status key
        const allActions = [];
        if (auditData?.categories) {
          CAT_ORDER.forEach(catKey => {
            const cat = auditData.categories[catKey];
            if (!cat) return;
            const recs = cat.recommendations || [];
            const catGap = Math.max(0, 100 - (cat.score ?? 100));
            const impactPerRec = recs.length > 0 ? Math.round(catGap / recs.length) : null;
            recs.forEach((rec, ri) => {
              const recTitle = (typeof rec === "object" ? rec?.title : "") || "";
              const key = `${brandId}__cat__${catKey}__${ri}`;
              // New = title not in previous audit AND user has never interacted with it
              const isNew = recTitle
                ? !prevTitles.has(recTitle.trim().toLowerCase()) && !(key in actionStatuses)
                : false;
              allActions.push({
                catKey,
                catLabel: CAT_LABELS[catKey] || cat.label,
                catScore: cat.score,
                calculatedImpact: impactPerRec,
                isNew,
                rec,
                key,
              });
            });
          });
        }

        // Sort actions
        const EFFORT_ORDER = { Low: 1, Medium: 2, High: 3 };
        const STATUS_ORDER = { open: 1, in_progress: 2, done: 3, ignore: 4 };
        const sortedActions = [...allActions].sort((a, b) => {
          const dir = actionSort.dir === "desc" ? -1 : 1;
          if (actionSort.col === "wirkung") {
            return dir * ((a.calculatedImpact ?? -1) - (b.calculatedImpact ?? -1));
          }
          if (actionSort.col === "aufwand") {
            const ea = EFFORT_ORDER[a.rec?.effort] ?? 0;
            const eb = EFFORT_ORDER[b.rec?.effort] ?? 0;
            return dir * (ea - eb);
          }
          if (actionSort.col === "status") {
            const sa = STATUS_ORDER[actionStatuses[a.key] || "open"] ?? 0;
            const sb = STATUS_ORDER[actionStatuses[b.key] || "open"] ?? 0;
            return dir * (sa - sb);
          }
          return 0;
        });

        const toggleSort = (col) => setActionSort(prev =>
          prev.col === col ? { col, dir: prev.dir === "desc" ? "asc" : "desc" } : { col, dir: "desc" }
        );
        const SortIcon = ({ col }) => {
          if (actionSort.col !== col) return <span style={{ opacity: 0.3, marginLeft: 3 }}>⇅</span>;
          return <span style={{ marginLeft: 3 }}>{actionSort.dir === "desc" ? "↓" : "↑"}</span>;
        };

        const counts = { open: 0, in_progress: 0, done: 0, ignore: 0 };
        allActions.forEach(a => {
          const st = actionStatuses[a.key] || "open";
          counts[st] = (counts[st] || 0) + 1;
        });

        const EFFORT_DE = { Low: t("dashboard.effortLow"), Medium: t("dashboard.effortMedium"), High: t("dashboard.effortHigh") };
        const effortColor = { Low: "#16a34a", Medium: "#d97706", High: "#dc2626" };
        // Abbreviate category label for the topic badge
        const CAT_ABBREV = {
          contentQuality: t("dashboard.catContentQuality"),
          authority:      t("dashboard.catAuthority"),
          technicalSeo:   t("dashboard.catTechnicalSeo"),
          engagement:     t("dashboard.catEngagement"),
        };

        return (
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "DM Mono,monospace", color: "#94a3b8", marginBottom: 4 }}>
                {t("dashboard.actEyebrow")}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>
                {t("dashboard.actTitle")}
              </div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                {t("dashboard.actSubtitle")}
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
                {STATUS_OPTIONS.map(s => (
                  <span key={s.value} style={{ fontSize: 12, color: "#64748b" }}>
                    <span style={{ fontWeight: 600, color: "#0f172a" }}>{counts[s.value] || 0}</span> {s.label}
                  </span>
                ))}
              </div>
            </div>

            {allActions.length === 0 ? (
              <div className="dash-no-data-section">
                {auditData
                  ? t("dashboard.actEmptyWithAudit")
                  : t("dashboard.actEmptyNoAudit")}
              </div>
            ) : (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "white" }}>
                {/* Column headers */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px 120px", gap: 0, padding: "8px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: 10, fontFamily: "DM Mono,monospace", letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>{t("dashboard.actColMassnahme")}</span>
                  {[["wirkung",t("dashboard.actColWirkung"),"right"],["aufwand",t("dashboard.actColAufwand"),"center"],["status",t("dashboard.actColStatus"),"center"]].map(([col, label, align]) => (
                    <span key={col} onClick={() => toggleSort(col)} style={{ fontSize: 10, fontFamily: "DM Mono,monospace", letterSpacing: "0.08em", textTransform: "uppercase", color: actionSort.col === col ? "#2563eb" : "#94a3b8", textAlign: align, cursor: "pointer", userSelect: "none" }}>
                      {label}<SortIcon col={col} />
                    </span>
                  ))}
                </div>
                {sortedActions.map((action, globalIdx) => {
                  const status = actionStatuses[action.key] || "open";
                  const rec = action.rec;
                  const recTitle = typeof rec === "object" ? rec?.title : "";
                  const recText  = typeof rec === "object" ? rec?.text  : (typeof rec === "string" ? rec : "—");
                  const effort      = typeof rec === "object" ? rec?.effort : null;
                  const impactScore = action.calculatedImpact;
                  const isDone   = status === "done";
                  const isIgnore = status === "ignore";
                  const eColor   = effortColor[effort] || "#64748b";
                  return (
                    <div key={action.key} style={{
                      display: "grid", gridTemplateColumns: "1fr 100px 110px 120px",
                      alignItems: "center", gap: 0,
                      padding: "14px 20px",
                      borderBottom: globalIdx < sortedActions.length - 1 ? "1px solid #f1f5f9" : "none",
                      opacity: isIgnore ? 0.4 : 1,
                      background: isDone ? "#f8fdf8" : "white",
                    }}>
                      {/* Left: topic badge (own line) + title + description */}
                      <div style={{ paddingRight: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                          <span style={{
                            fontSize: 10, fontFamily: "DM Mono,monospace", letterSpacing: "0.04em",
                            padding: "1px 7px", borderRadius: 3,
                            background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0",
                            whiteSpace: "nowrap",
                          }}>{CAT_ABBREV[action.catKey]}</span>
                          {action.isNew && (
                            <span style={{
                              fontSize: 10, fontFamily: "DM Mono,monospace", letterSpacing: "0.06em",
                              padding: "1px 7px", borderRadius: 3,
                              background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
                              whiteSpace: "nowrap",
                            }}>{t("dashboard.badgeNeu")}</span>
                          )}
                        </div>
                        {recTitle && (
                          <div style={{ fontSize: 13, fontWeight: 600, color: isDone ? "#94a3b8" : "#0f172a", textDecoration: isDone ? "line-through" : "none", marginBottom: 3 }}>
                            {recTitle}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
                          {!recTitle && isDone ? <s>{recText}</s> : recText}
                        </div>
                      </div>
                      {/* Impact score */}
                      <div style={{ textAlign: "right", paddingRight: 12 }}>
                        {impactScore != null ? (
                          <div>
                            <div style={{ fontSize: 20, fontWeight: 700, color: "#16a34a", fontFamily: "DM Mono,monospace" }}>+{impactScore}</div>
                            <div style={{ fontSize: 9, fontFamily: "DM Mono,monospace", letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8", marginTop: 1 }}>{t("dashboard.actImpactLabel")}</div>
                          </div>
                        ) : (
                          <span style={{ color: "#e2e8f0", fontSize: 13 }}>—</span>
                        )}
                      </div>
                      {/* Effort */}
                      <div style={{ textAlign: "center" }}>
                        {effort ? (
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: eColor }}>{EFFORT_DE[effort] || effort}</div>
                            <div style={{ fontSize: 9, fontFamily: "DM Mono,monospace", letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8", marginTop: 1 }}>{t("dashboard.actColAufwand")}</div>
                          </div>
                        ) : (
                          <span style={{ color: "#e2e8f0", fontSize: 13 }}>—</span>
                        )}
                      </div>
                      {/* Status */}
                      <div style={{ textAlign: "center" }}>
                        <select
                          className={`act-status-select act-status-${status}`}
                          value={status}
                          onChange={e => updateActionStatus(action.key, e.target.value)}
                        >
                          {STATUS_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {auditData && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#94a3b8" }}>
                {t("dashboard.actFooterAudit")} {new Date(auditData.created_at).toLocaleDateString(DATE_LOCALE[lang] || "de-CH")}
                {" · "}{t("dashboard.actFooterNote")}
              </div>
            )}
          </div>
        );
      })()}

      {activeTab === "keyPrompts" && (
        <KeyPromptsTab brandId={brandId} brandUrl={brand?.website_url} />
      )}
    </div>
  );
}
